import { AppEnvironments, Environment } from "@/environment";
import { SocketStock } from "@/helpers/models/stock/socket_stock.model";
import countriesJSON from "../assets/countries_list.json";
import {
  StockMarketColors,
  StockMarkets,
} from "@/helpers/models/stock/stocks.model";
import feelingsJSON from "../assets/json/feelings.json";
import { Ref, ref } from "vue";
import { TYPE, useToast, POSITION } from "vue-toastification";
import storageService from "./storage.service";
import { openModal, pushModal, popModal } from "jenesius-vue-modal";
import ConfirmationDialogComponent from "@/components/ConfirmationDialogComponent.vue";
import { ConfigurationModel } from "@/views/private/timeline/models/configuration.model";
import httpService from "./http.service";
import User from "@/views/private/timeline/models/user.model";
import axios from "axios";
import { GeneralStockModel } from "@/views/private/timeline/models/generalstock.model";
import PostsModel from "@/views/private/timeline/models/posts.model";
import GeneralUserModel from "@/views/private/timeline/models/generaluser.model";
import ChartTypesJSON from "@/assets/json/chart_types.json";
import router from "@/router/router";
import { setPostLoginRedirect } from "./keycloak.service";
import { RouteEnum } from "@/router/routeNames";
import { isAccountBannedSession } from "@/helpers/accountBannedSession";
import { isAccountDeletedSession } from "@/helpers/accountDeletedSession";
import AlertDeleteComponent from "@/components/AlertDeleteComponent.vue";
import ConnectionModel from "@/helpers/models/connection.model";
import { userProfileUser } from "@/views/private/timeline/composables/profileuser.composable";
import {
  NewsFeedIds,
  NewsSearchDTO,
  NewsSourceLogos,
  NewsSourceTitles,
} from "../helpers/models/news-search.model";
import { useStockSocket } from "@/composables/stock_socket.composable";
import i18n from "@/locale/index";

class UtilService {
  chartColors: any[] = [];
  apiUrl: any =
    "https://facts.traderverse.io/markets/api/v3/discover/sector_performance";
  newsSources: NewsSearchDTO[] = [
    new NewsSearchDTO(
      NewsFeedIds.yahoo,
      NewsSourceTitles.yahoo,
      NewsSourceLogos.yahoo
    ),
    new NewsSearchDTO(
      NewsFeedIds.marketWatch,
      NewsSourceTitles.marketWatch,
      NewsSourceLogos.marketWatch
    ),
    new NewsSearchDTO(
      NewsFeedIds.cryptoDaily,
      NewsSourceTitles.cryptoDaily,
      NewsSourceLogos.cryptoDaily
    ),
    new NewsSearchDTO(
      NewsFeedIds.cointelegraph,
      NewsSourceTitles.cointelegraph,
      NewsSourceLogos.cointelegraph
    ),
  ];

  // Global flag to track if we're in logout/session expiry flow
  private isLoggingOut: boolean = false;

  constructor() {
    for (let i = 0; i < 12; i++) {
      this.chartColors.push(this.generateRandomLightColor());
    }
  }

  debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number,
    immediate: boolean = false
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null;

    return function executedFunction(...args: Parameters<T>) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };

      const callNow = immediate && !timeout;

      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(later, wait);

      if (callNow) func(...args);
    };
  }

  throttle<T extends (...args: any[]) => any>(
    func: T,
    wait: number,
    immediate: boolean = false
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null;
    let lastArgs: Parameters<T> | null = null;

    return function executedFunction(...args: Parameters<T>) {
      // If immediate and no timer, call now
      if (immediate && !timeout) {
        func(...args);
      } else {
        // Otherwise store latest args to call at end of throttle window
        lastArgs = args;
      }

      if (!timeout) {
        timeout = setTimeout(() => {
          timeout = null;
          // Call function if trailing call required
          if (!immediate && lastArgs) {
            func(...lastArgs);
            lastArgs = null;
          }
        }, wait);
      }
    };
  }
  // Add this method to your UtilService class
  shallowEqual(objA: any, objB: any): boolean {
    if (objA === objB) {
      return true;
    }

    if (
      typeof objA !== "object" ||
      objA === null ||
      typeof objB !== "object" ||
      objB === null
    ) {
      return false;
    }

    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);

    if (keysA.length !== keysB.length) {
      return false;
    }

    for (let key of keysA) {
      if (!objB.hasOwnProperty(key) || objA[key] !== objB[key]) {
        return false;
      }
    }

    return true;
  }
  generateRandomLightColor = () => {
    let color = "#";
    for (let i = 0; i < 3; i++)
      color += (
        "0" +
        Math.floor(((1 + Math.random()) * Math.pow(16, 2)) / 2).toString(16)
      ).slice(-2);
    return color;
  };

  goToStockScreen = (stockId: string, market: string = "stocks") => {
    router.push({
      name: market,
      params: { stock: stockId },
    });
  };

  showToast(
    message: string,
    type: TYPE = TYPE.SUCCESS,
    duration: number = 2500
  ) {
    // Suppress error toasts during logout flow, except for the session expiry message
    // if (this.isLoggingOut && type === TYPE.ERROR && !message.includes("Session expired")) {
    //   console.log("Suppressed error toast during logout:", message);
    //   return;
    // }

    try {
      const toast = useToast();
      const toastOptions = {
        timeout: duration,
        position: POSITION.TOP_RIGHT,
        hideProgressBar: true,
        icon: false,
      };
      switch (type) {
        case TYPE.SUCCESS:
          toast.success(message, toastOptions);
          break;
        case TYPE.ERROR:
          toast.error(message, toastOptions);
          break;
        case TYPE.WARNING:
          toast.warning(message, toastOptions);
          break;
        case TYPE.INFO:
          toast.info(message, toastOptions);
          break;
        default:
          toast(message, toastOptions);
          break;
      }
    } catch (e) {
      console.error("Error showing toast:", e);
    }
  }

  coverImageError = () => this.getUserProfilePicture("", 0, "", true, true);

  get FirebaseConfig() {
    if (Environment.currentEnvironment == AppEnvironments.production) {
      return {
        apiKey: "AIzaSyCrkiZKoUgjzr4MngmlE76MqK7xGLv76AU",
        authDomain: "traderverse-75f26.firebaseapp.com",
        databaseURL: "https://traderverse-75f26-default-rtdb.firebaseio.com",
        projectId: "traderverse-75f26",
        storageBucket: "traderverse-75f26.appspot.com",
        messagingSenderId: "181344263879",
        appId: "1:181344263879:web:42efa1997fbf2e22e9a8e2",
        measurementId: "G-H3C5TY7Z06",
      };
    } else if (
      Environment.currentEnvironment == AppEnvironments.uat ||
      Environment.currentEnvironment == AppEnvironments.api6uat
    ) {
      // return {
      //   apiKey: "AIzaSyAv97aPBbEHkdLXog6DMqpvVeVnY1BmZGE",
      //   authDomain: "traderverse-uat.firebaseapp.com",
      //   databaseURL: "https://traderverse-uat-default-rtdb.firebaseio.com",
      //   projectId: "traderverse-uat",
      //   storageBucket: "traderverse-uat.firebasestorage.app",
      //   messagingSenderId: "754088550830",
      //   appId: "1:754088550830:web:402394ecb1718e2501683f",
      //   measurementId: "G-Y1JTS2PG90",
      // };
      return {
        apiKey: "AIzaSyCrkiZKoUgjzr4MngmlE76MqK7xGLv76AU",
        authDomain: "traderverse-75f26.firebaseapp.com",
        databaseURL: "https://traderverse-75f26-default-rtdb.firebaseio.com",
        projectId: "traderverse-75f26",
        storageBucket: "traderverse-75f26.appspot.com",
        messagingSenderId: "181344263879",
        appId: "1:181344263879:web:42efa1997fbf2e22e9a8e2",
        measurementId: "G-H3C5TY7Z06",
      };
    } else if (Environment.currentEnvironment == AppEnvironments.qa) {
      return {
        apiKey: "AIzaSyBzXoK4OB_3UnxSWKy6b3yUKdoNo74ZjtI",
        authDomain: "traderverse-qa-test.firebaseapp.com",
        projectId: "traderverse-qa-test",
        storageBucket: "traderverse-qa-test.firebasestorage.app",
        messagingSenderId: "38957765773",
        appId: "1:38957765773:web:fb9d8e1fc783fdad303ec4",
      };
    } else if (Environment.currentEnvironment == AppEnvironments.stage) {
      return {
        apiKey: "AIzaSyCrkiZKoUgjzr4MngmlE76MqK7xGLv76AU",
        authDomain: "traderverse-75f26.firebaseapp.com",
        databaseURL: "https://traderverse-75f26-default-rtdb.firebaseio.com",
        projectId: "traderverse-75f26",
        storageBucket: "traderverse-75f26.appspot.com",
        messagingSenderId: "181344263879",
        appId: "1:181344263879:web:42efa1997fbf2e22e9a8e2",
        measurementId: "G-H3C5TY7Z06",
      };
    } else if (Environment.currentEnvironment == AppEnvironments.dev) {
      return {
        apiKey: "AIzaSyDQGiP1NMRy0xZ3-c-D89mLIDTr0mtfT5c",
        authDomain: "traderverse-prd.firebaseapp.com",
        databaseURL: "https://traderverse-prd-default-rtdb.firebaseio.com",
        projectId: "traderverse-prd",
        storageBucket: "traderverse-prd.appspot.com",
        messagingSenderId: "449478086469",
        appId: "1:449478086469:web:4aac44ff8b73d7a726b82c",
        measurementId: "G-0PKPFY359M",
      };
    } else if (Environment.currentEnvironment == AppEnvironments.devSix) {
      return {
        apiKey: "AIzaSyDQGiP1NMRy0xZ3-c-D89mLIDTr0mtfT5c",
        authDomain: "traderverse-prd.firebaseapp.com",
        databaseURL: "https://traderverse-prd-default-rtdb.firebaseio.com",
        projectId: "traderverse-prd",
        storageBucket: "traderverse-prd.appspot.com",
        messagingSenderId: "449478086469",
        appId: "1:449478086469:web:4aac44ff8b73d7a726b82c",
        measurementId: "G-0PKPFY359M",
      };
    } else if (Environment.currentEnvironment == AppEnvironments.internal) {
      return {
        apiKey: "AIzaSyBqvX0JLJGyjGIa0r2ut58pvP2GP6wpc8Y",
        authDomain: "internaldev-361116.firebaseapp.com",
        databaseURL: "https://internaldev-361116-default-rtdb.firebaseio.com",
        projectId: "internaldev-361116",
        storageBucket: "internaldev-361116.appspot.com",
        messagingSenderId: "740511496249",
        appId: "1:740511496249:web:d4975e6d15315884c3f5e8",
        measurementId: "G-M6R0R5WRJ8",
      };
    }
    return {
      apiKey: "AIzaSyCrkiZKoUgjzr4MngmlE76MqK7xGLv76AU",
      authDomain: "traderverse-75f26.firebaseapp.com",
      databaseURL: "https://traderverse-75f26-default-rtdb.firebaseio.com",
      projectId: "traderverse-75f26",
      storageBucket: "traderverse-75f26.appspot.com",
      messagingSenderId: "181344263879",
      appId: "1:181344263879:web:42efa1997fbf2e22e9a8e2",
      measurementId: "G-H3C5TY7Z06",
    };
  }

  getImageUrl = (url: string, directory: string | null) => {
    try {
      var images = require.context("../assets/images", true);
      var imageUrl = directory ? `./${directory}/${url}` : `./${url}`;
      return images(imageUrl);
    } catch (e) {
      return "";
    }
  };

  getConnectionsByKeyword = async (connectionType: string, keyword: string) => {
    if (keyword.length) {
      const response: any = await httpService.searchConnectionByType(
        connectionType,
        keyword
      );
      if (
        response.data.isSuccess &&
        response.data.data &&
        response.data.data.length
      ) {
        return response.data.data.map((x: any) => {
          var user = new GeneralUserModel();
          user = Object.assign(user, x);
          return user;
        });
      }
    }
    return [];
  };

  getSubscriptionsByKeyword = async (keyword: string) => {
    if (keyword.length) {
      const response: any = await httpService.searchSubscriptions(keyword);
      if (response.isSuccess && response.data && response.data.length) {
        return response.data.data;
      }
    }
    return [];
  };

  // getStockCryptoByKeyword = async (keyword: string) => {
  //   if (keyword.length) {
  //     const response: any = await httpService.searchStockCrypto(keyword);
  //     if (
  //       response.data.isSuccess &&
  //       response.data.data &&
  //       response.data.data.length
  //     ) {
  //       return response.data.data.map((x: any) => {
  //         var user = new GeneralStockModel();
  //         user = Object.assign(user, x);
  //         return user;
  //       });
  //     }
  //   }
  //   return [];
  // };

  getCountriesJSON() {
    return countriesJSON;
  }

  getShortCodeOfCountry(countryName: string) {
    if (countryName) {
      let countries = this.getCountriesJSON();
      let country = countries.find((x) => x.name == countryName);
      if (country && country.code) {
        return country.code;
      }
    }
    return "";
  }

  getUserProfilePicture = (
    userId: string,
    count: number,
    userGender: string,
    isCoverPicture: boolean = false,
    showDummy: boolean = true
  ) => {
    const type = isCoverPicture ? "coverphoto" : "profilepicture";
    if (count > 0) {
      let img;
      img = `https://imagedelivery.net/09I_Y_JIwzvjUiGrV3dv3Q/images/${type}_${userId}_v_${count}/public`;
      // img = `https://firebasestorage.googleapis.com/v0/b/${this.FirebaseConfig.storageBucket}/o/images%2F${type}_${userId}_v_${count}?alt=media`;

      return img;
    } else if (showDummy) {
      if (isCoverPicture) {
        return this.getImageUrl("dummy-cover.png", null);
        // return `https://firebasestorage.googleapis.com/v0/b/${this.FirebaseConfig.storageBucket}/o/images%2F${type}_${userId}_v_${count}?alt=media`
      } else {
        // if (userGender == "Male") {
        return this.getImageUrl("avatar_men.png", null);
        // } else {
        //   return this.getImageUrl("avatar_women.png", null);
        // }
      }
    } else {
      return "";
    }
  };

  getSectionImage = (userId: string, sectionName: string, type: string) => {
    // return `https://firebasestorage.googleapis.com/v0/b/${this.FirebaseConfig.storageBucket}/o/share_${userId}_${type}_${sectionName}.png?alt=media`;
    return `https://imagedelivery.net/09I_Y_JIwzvjUiGrV3dv3Q/images/${userId}_${type}_${sectionName}.png/public`;
  };

  get Host(): string {
    if (Environment.currentEnvironment == AppEnvironments.stage) {
      return "https://social-stage.traderverse.io/";
    } else if (Environment.currentEnvironment == AppEnvironments.dev) {
      return "https://profiles-develop.traderverse.io/";
    } else if (Environment.currentEnvironment == AppEnvironments.uat) {
      return "https://profiletest-uat.traderverse.io/";
    } else if (Environment.currentEnvironment == AppEnvironments.api6uat) {
      return "https://api-uat.traderverse.io/";
    } else if (Environment.currentEnvironment == AppEnvironments.production) {
      return "https://app.traderverse.io/";
    } else if (Environment.currentEnvironment == AppEnvironments.internal) {
      return "https://internal-profile.traderverse.io/";
    }
    return "https://profiles.traderverse.io/";
  }

  getHostURLWithoutProtocol() {
    var url = process.env.VUE_APP_API_URL;
    if (!url || !url.includes("https://")) {
      return "https://" + (url || "");
    }
    throw new Error("VUE_APP_API_URL is not defined in environment variables");
  }

  getHostURL() {
    const resolvedHostByEnvironment = (() => {
      if (Environment.currentEnvironment == AppEnvironments.stage) {
        return "social-stage.traderverse.io";
      } else if (Environment.currentEnvironment == AppEnvironments.dev) {
        return "profiles-develop.traderverse.io";
      } else if (
        Environment.currentEnvironment == AppEnvironments.uat ||
        Environment.currentEnvironment == AppEnvironments.api6uat
      ) {
        return "api-uat.traderverse.io";
      } else if (Environment.currentEnvironment == AppEnvironments.qa) {
        return "qa-test.traderverse.io";
      } else if (Environment.currentEnvironment == AppEnvironments.internal) {
        return "internal-profile.traderverse.io";
      } else if (Environment.currentEnvironment == AppEnvironments.production) {
        return "app-api.traderverse.io";
      }
      return null;
    })();

    if (resolvedHostByEnvironment) {
      return `wss://${resolvedHostByEnvironment}`;
    }

    if (process.env.VUE_APP_API_URL) {
      return "wss://" + process.env.VUE_APP_API_URL;
    }

    return `wss://${location.host}/`;
  }

  getLiveHostURL() {
    var url = this.getHostURL();
    if (url.includes("localhost")) {
      return this.Host;
    }
    return url;
  }

  getStockImage = (
    stockId: string,
    type: StockMarkets = StockMarkets.stocks
  ) => {
    // Always resolve via media.traderverse.io (equity/crypto asset SVGs)
    return this.getStockCDNImage("", stockId, type);
  };

  validatePassword = (password: string): string => {
    const t = i18n.global.t;
    // 1. Minimum Length (8 characters)
    if (password.length < 8) {
      return t("auth.passwordReqs.minLength");
    }

    // 2. No Spaces
    if (password.includes(" ")) {
      return t("auth.passwordReqs.noSpaces");
    }

    // 3. At least one Number (0-9)
    var checkNumberExistence = /[0-9]/;
    if (!checkNumberExistence.test(password)) {
      return t("auth.passwordReqs.oneNumber");
    }

    // 4. At least one Lowercase letter (a-z)
    var checkSmallAlphabetExistence = /[a-z]/;
    if (!checkSmallAlphabetExistence.test(password)) {
      return t("auth.passwordReqs.oneLowercase");
    }

    // 5. At least one Uppercase letter (A-Z)
    var checkLargeAlphabetExistence = /[A-Z]/;
    if (!checkLargeAlphabetExistence.test(password)) {
      return t("auth.passwordReqs.oneUppercase");
    }

    // 6. At least one Special Character (!@#$%^&*(),.?":{}|<> - based on your existing regex)
    var checkSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;
    if (!checkSpecialChar.test(password)) {
      return t("auth.passwordReqs.oneSpecialChar");
    }

    // If all checks pass
    return "";
  };

  getPasswordRequirements = () => {
    return `Password must:
    • Be at least 8 characters long
    • Not contain spaces
    • Include at least one number (0-9)
    • Include at least one lowercase letter (a-z)
    • Include at least one uppercase letter (A-Z)
    • Include at least one special character (!@#$%^&*(),.?":{}|<>)`;
  };

  clampText = (
    textElem: any,
    field: Ref<boolean>,
    value: boolean,
    maxLines: number = 3
  ) => {
    if (textElem != null) {
      if (value) {
        // Calculate approximate line height
        const computedStyle = window.getComputedStyle(textElem);
        const lineHeight =
          parseInt(computedStyle.lineHeight) ||
          parseInt(computedStyle.fontSize) * 1.4;
        const maxHeight = lineHeight * maxLines;

        // Check if content exceeds max lines
        if (textElem.scrollHeight > maxHeight) {
          field.value = true;
          textElem.classList.add("truncate");
          textElem.classList.remove("nonTruncate");
        } else {
          field.value = false;
          textElem.classList.add("nonTruncate");
          textElem.classList.remove("truncate");
        }
      } else {
        field.value = false;
        textElem.classList.add("nonTruncate");
        textElem.classList.remove("truncate");
      }
    }
  };

  updateScrollbarWidth(content: any, scrollbar: any) {
    const visibleWidth = content.clientWidth;
    const totalWidth = content.scrollWidth;
    const ratio = visibleWidth / totalWidth;
    const scrollbarWidth = ratio * visibleWidth;

    scrollbar.style.width = `${scrollbarWidth}px`;
  }

  connectScrollbarAndContent(content: any, scrollbar: any) {
    content.addEventListener("scroll", function () {
      const scrollPercentage =
        (content.scrollLeft / (content.scrollWidth - content.clientWidth)) *
        100;
      const scrollbarPosition =
        (scrollPercentage * (content.clientWidth - scrollbar.clientWidth)) /
        100;
      scrollbar.style.left = `${scrollbarPosition}px`;
    });

    scrollbar.addEventListener("mousedown", function (event: any) {
      event.preventDefault();

      const startX = event.clientX;
      const initialScrollLeft = content.scrollLeft;

      function dragMove(e: any) {
        const deltaX = e.clientX - startX;
        const percentage =
          deltaX / (content.clientWidth - scrollbar.clientWidth);
        content.scrollLeft =
          initialScrollLeft +
          percentage * (content.scrollWidth - content.clientWidth);
      }

      function dragEnd() {
        document.removeEventListener("mousemove", dragMove);
        document.removeEventListener("mouseup", dragEnd);
      }

      document.addEventListener("mousemove", dragMove);
      document.addEventListener("mouseup", dragEnd);
    });
    this.updateScrollbarWidth(content, scrollbar);
  }

  showConfirmationDialog(msg: string = "Are you sure?") {
    return new Promise(async (resolve, reject) => {
      var modal = await pushModal(ConfirmationDialogComponent, {
        message: msg,
      });
      if (modal) {
        modal.on("cancel", () => {
          resolve(false);
          modal.close();
        });
        modal.on("confirm", () => {
          resolve(true);
          modal.close();
        });
      }
    });
  }

  isDragStart: boolean = false;

  draggableScroll(ele: any, scrollbar: any) {
    try {
      if (ele && ele.length) {
        ele = ele[0];
      }
      if (ele) {
        this.connectScrollbarAndContent(ele, scrollbar);
        ele.style.cursor = "grab";
        this.checkOverflow(ele);
        var self = this;
        let pos = { top: 0, left: 0, x: 0, y: 0 };

        const mouseDownHandler = function (e: any) {
          if (!self.isDragStart) {
            e.stopPropagation();
            ele.style.cursor = "grabbing";
            ele.style.userSelect = "none";

            pos = {
              left: ele.scrollLeft,
              top: ele.scrollTop,
              // Get the current mouse position
              x: e.clientX,
              y: e.clientY,
            };

            document.addEventListener("mousemove", mouseMoveHandler);
            document.addEventListener("mouseup", mouseUpHandler);
          }
        };

        const mouseMoveHandler = function (e: any) {
          if (!self.isDragStart) {
            e.stopPropagation();
            // How far the mouse has been moved
            const dx = e.clientX - pos.x;
            // const dy = e.clientY - pos.y;

            // Scroll the element
            // ele.scrollTop = pos.top - dy;
            ele.scrollLeft = pos.left - dx;
          }
        };

        const mouseUpHandler = function () {
          if (!self.isDragStart) {
            ele.style.cursor = "grab";
            ele.style.removeProperty("user-select");

            document.removeEventListener("mousemove", mouseMoveHandler);
            document.removeEventListener("mouseup", mouseUpHandler);
          }
        };

        // Attach the handler
        ele.addEventListener("mousedown", mouseDownHandler);
      }
    } catch (e) {}
  }

  onDraggableDragEvent(type: "start" | "stop") {
    if (type == "start") {
      this.isDragStart = true;
      // setTimeout(() => {
      //   this.isDragStart = false;
      // }, 4000);
    } else {
      this.isDragStart = false;
    }
  }

  checkOverflow(ele: HTMLDivElement) {
    var hasOverflow = ele.scrollWidth > ele.clientWidth;
    if (hasOverflow) {
      ele.parentElement!.classList.add("canScroll");
    } else {
      ele.parentElement!.classList.remove("canScroll");
    }
  }

  showGuestUserFlow() {
    this.showToast("Please! Sign in to continue", TYPE.WARNING);
    storageService.RemoveAllProperties();
    setTimeout(() => {
      window.open("/signin", "_self");
    }, 100);
  }

  getStocksBar(
    stocks: SocketStock[],
    areaWidth: number,
    defaultPercentage: number = 4
  ) {
    var totalData: {
      title: string;
      count: number;
      color: string;
      maxWidth: any;
      percent: string;
    }[] = [
      {
        title: StockMarkets.stocks,
        count: 0,
        color: StockMarketColors.stocks,
        maxWidth: defaultPercentage,
        percent: "",
      },
      {
        title: StockMarkets.crypto,
        count: 0,
        color: StockMarketColors.crypto,
        maxWidth: defaultPercentage,
        percent: "",
      },
      {
        title: StockMarkets.etf,
        count: 0,
        color: StockMarketColors.etf,
        maxWidth: defaultPercentage,
        percent: "",
      },
      {
        title: StockMarkets.Futures,
        count: 0,
        color: StockMarketColors.Futures,
        maxWidth: defaultPercentage,
        percent: "",
      },
      {
        title: StockMarkets.nft,
        count: 0,
        color: StockMarketColors.nft,
        maxWidth: defaultPercentage,
        percent: "",
      },
      {
        title: StockMarkets.indices,
        count: 0,
        color: StockMarketColors.indices,
        maxWidth: defaultPercentage,
        percent: "",
      },
      {
        title: StockMarkets.options,
        count: 0,
        color: StockMarketColors.options,
        maxWidth: defaultPercentage,
        percent: "",
      },
    ];
    if (stocks) {
      var totalCount = stocks.length;
      var oneColWidth = areaWidth / totalCount;
      var oneColSpace = (oneColWidth / areaWidth) * 100;
      for (let market of totalData) {
        market.count = stocks.filter(
          (x) => x.type?.toLowerCase() == market.title?.toLowerCase()
        ).length;
        market.maxWidth = oneColSpace * market.count;
        market.percent = ((market.count / stocks.length) * 100).toFixed(2);
      }

      totalData.sort((a, b) => b.count - a.count);
    }
    return totalData;
  }

  getTradeColor(key: string) {
    if (key == "open") {
      return "#D667FF";
    } else if (key == "high") {
      return "#A3FF99";
    } else if (key == "low") {
      return "#FF8080";
    } else if (key == "close") {
      return "#FFC477";
    }
  }

  getAttributeColor(name: string) {
    var item = this.attributesList.find((x) => x.name == name);
    if (item) {
      return item.color;
    }
  }

  safeRound(value, decimals = 2) {
    if (value == undefined || value == null || isNaN(value) || value === 0) {
      return 0;
    }
    return parseFloat(Number(value).toFixed(decimals));
  }

  benchmarkingDummyData: Object = {
    _id: "1703177802499",
    modifiedBy: "",
    modifiedOn: 1703630831655,
    name: "Benchmarking",
    order: 9,
    type: "benchmarking",
    userId: "1661449528310",
    data: {
      list: [
        {
          stockId: "TSLA",
          symbol: "Tesla, Inc.",
          name: "Tesla, Inc.",
          percentage: 728.57,
          percentageTitle: "Last 3M P&L",
          type: "portfolio",
          title: "Portfolio",
          logoUrl: "https://media.traderverse.io/assets/equity/TSLA_NASDAQ.svg",
          tradeDate: 1703630694025,
          data: [
            {
              name: "high",
              data: [
                [1698969600000, "2335.0000"],
                [1698883200000, "2324.0000"],
                [1698796800000, "2317.5000"],
                [1698710400000, "2332.9500"],
                [1698624000000, "2324.6001"],
              ],
            },
            {
              name: "low",
              data: [
                [1698969600000, "2314.8999"],
                [1698883200000, "2306.5000"],
                [1698796800000, "2275.2500"],
                [1698710400000, "2282.8000"],
                [1698624000000, "2272.0000"],
              ],
            },
          ],
          description:
            "Commutative Profit Ratio - Cumulative Profit/Adjusted Total Beginning Assets",
        },
        {
          stockId: "NVDA",
          symbol: "NVIDIA Corporation",
          name: "NVIDIA Corporation",
          percentage: 728.57,
          percentageTitle: "Last 3M P&L",
          type: "portfolio",
          title: "Portfolio",
          logoUrl: "https://media.traderverse.io/assets/equity/NVDA_NASDAQ.svg",
          tradeDate: 1703630694025,
          data: [
            {
              name: "high",
              data: [
                [1698969600000, "2335.0000"],
                [1698883200000, "2324.0000"],
                [1698796800000, "2317.5000"],
                [1698710400000, "2332.9500"],
                [1698624000000, "2324.6001"],
              ],
            },
            {
              name: "low",
              data: [
                [1698969600000, "2314.8999"],
                [1698883200000, "2306.5000"],
                [1698796800000, "2275.2500"],
                [1698710400000, "2282.8000"],
                [1698624000000, "2272.0000"],
              ],
            },
          ],
          description:
            "Commutative Profit Ratio - Cumulative Profit/Adjusted Total Beginning Assets",
        },
        {
          stockId: "MSFT",
          symbol: "Microsoft Corporation",
          name: "Microsoft Corporation",
          percentage: 728.57,
          percentageTitle: "Last 3M P&L",
          type: "portfolio",
          title: "Portfolio",
          logoUrl: "https://media.traderverse.io/assets/equity/MSFT_NASDAQ.svg",
          tradeDate: 1703630694025,
          data: [
            {
              name: "high",
              data: [
                [1698969600000, "2335.0000"],
                [1698883200000, "2324.0000"],
                [1698796800000, "2317.5000"],
                [1698710400000, "2332.9500"],
                [1698624000000, "2324.6001"],
              ],
            },
            {
              name: "low",
              data: [
                [1698969600000, "2314.8999"],
                [1698883200000, "2306.5000"],
                [1698796800000, "2275.2500"],
                [1698710400000, "2282.8000"],
                [1698624000000, "2272.0000"],
              ],
            },
          ],
          description:
            "Commutative Profit Ratio - Cumulative Profit/Adjusted Total Beginning Assets",
        },
      ],
    },
  };
  predictionOHLCDummyData: Object = {
    _id: "1704129205105",
    modifiedBy: "",
    modifiedOn: 1704216238104,
    name: "Predictions OHLC",
    order: 10,
    type: "predictionsOHLC",
    userId: "1703694870502",

    data: {
      list: [
        {
          stockId: "BTC_CRYPTO",
          symbol: "BTC",
          name: "Bitcoin US",
          changesPercentage: 2.4762,
          change: 1094.16,
          purchased: {
            date: "2024-01-01T08:00:00.000Z",
            quantity: "1",
            price: "45000",
          },

          price: 142500,
          results: {
            ratio: "2/4",
            accuracy: 50,
          },
          ohlc: [
            {
              type: "open",
              name: "Open",
              price: 143.7,
              percentage: 2.45001,
              id: 10001,
            },
            {
              type: "high",
              name: "High",
              price: 151.4,
              percentage: 5.5001,
              id: 10001,
            },
            {
              type: "low",
              name: "Low",
              price: 140.2,
              percentage: -1.2,
              id: 10001,
            },
            {
              type: "close",
              name: "Close",
              price: 145.6,
              percentage: 1.7004,
              id: 10001,
            },
          ],
          type: "crypto",
          logoUrl: "https://media.traderverse.io/assets/crypto/BTC_CRYPTO.svg",
          sold: {
            date: "2024-01-02T08:00:00.000Z",
            quantity: "1",
            price: "45001",
          },
        },
      ],
    },
  };
  dummyStocks: any[] = [
    {
      _id: "MSFT_NASDAQ",
      symbol: "MSFT",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/MSFT_NASDAQ.svg",
    },
    {
      _id: "NVDA_NASDAQ",
      symbol: "NVDA",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/NVDA_NASDAQ.svg",
    },
    {
      _id: "AAPL_NASDAQ",
      symbol: "AAPL",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/AAPL_NASDAQ.svg",
    },
    {
      _id: "ADBE_NASDAQ",
      symbol: "ADBE",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/ADBE_NASDAQ.svg",
    },
    {
      _id: "INTC_NASDAQ",
      symbol: "INTC",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/INTC_NASDAQ.svg",
    },
    {
      _id: "NFLX_NASDAQ",
      symbol: "NFLX",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/NFLX_NASDAQ.svg",
    },
    {
      _id: "PEP_NASDAQ",
      symbol: "PEP",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/PEP_NASDAQ.svg",
    },
    {
      _id: "EBAY_NASDAQ",
      symbol: "EBAY",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/EBAY_NASDAQ.svg",
    },
    {
      _id: "META_NASDAQ",
      symbol: "META",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/META_NASDAQ.svg",
    },
    {
      symbol: "AMZN",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/AMZN_NASDAQ.svg",
    },
    {
      symbol: "TSLA",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/TSLA_NASDAQ.svg",
    },
    {
      _id: "CASY_NASDAQ",
      symbol: "CASY",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/CASY_NASDAQ.svg",
    },
    {
      symbol: "AVGO",
      market: "stocks",
      logoUrl: "https://media.traderverse.io/assets/equity/AVGO_NASDAQ.svg",
    },
  ];
  dummyTransactions: any[] = [
    {
      _id: "1690564105058",
      companyName: "Microsoft Corporation Common Stock",
      createdOn: 1690564105058,
      date: "2023-05-18",
      logoUrl: "https://media.traderverse.io/assets/equity/MSFT_NASDAQ.svg",
      modifiedBy: "",
      modifiedOn: 1690564105059,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105058",
      quantity: 4000,
      rate: 338.79,
      remaining: 4000,
      stockType: "stocks",
      ticker: "MSFT",
      type: "buy",
      undefined: null,
    },
    {
      _id: "1690564105080",
      companyName: "Bitcoin",
      createdOn: 1690564105080,
      date: "2023-06-16",
      logoUrl: "https://media.traderverse.io/assets/crypto/BTC_CRYPTO.svg",
      modifiedBy: "",
      modifiedOn: 1690564105080,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105080",
      quantity: 700,
      rate: 29345.92,
      remaining: 700,
      stockType: "crypto",
      ticker: "BTC",
      type: "buy",
      undefined: null,
    },
    {
      _id: "1690564105093",
      companyName: "Amazon.com, Inc. Common Stock",
      createdOn: 1690564105093,
      date: "2023-07-14",
      logoUrl: "https://media.traderverse.io/assets/equity/AMZN_NASDAQ.svg",
      modifiedBy: "",
      modifiedOn: 1690564105094,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105093",
      quantity: 6000,
      rate: 132.31,
      remaining: 6000,
      stockType: "stocks",
      ticker: "AMZN",
      type: "buy",
      undefined: null,
    },
    {
      _id: "1690564105107",
      companyName: "Ethereum",
      createdOn: 1690564105107,
      date: "2023-06-26",
      logoUrl: "https://media.traderverse.io/assets/crypto/ETH_CRYPTO.svg",
      modifiedBy: "",
      modifiedOn: 1690564105107,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105107",
      quantity: 5000,
      rate: 1876.09,
      remaining: 5000,
      stockType: "crypto",
      ticker: "ETH",
      type: "buy",
      undefined: null,
    },
    {
      _id: "1690564105119",
      companyName: "Apple Inc. Common Stock",
      createdOn: 1690564105119,
      date: "2023-06-23",
      logoUrl: "https://media.traderverse.io/assets/equity/AAPL_NASDAQ.svg",
      modifiedBy: "",
      modifiedOn: 1690564105119,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105119",
      quantity: 2000,
      rate: 196.505,
      remaining: 2000,
      stockType: "stocks",
      ticker: "AAPL",
      type: "buy",
      undefined: null,
    },
    {
      _id: "1690564105133",
      companyName: "NVIDIA Corporation Common Stock",
      createdOn: 1690564105133,
      date: "2023-06-29",
      logoUrl: "https://media.traderverse.io/assets/equity/NVDA_NASDAQ.svg",
      modifiedBy: "",
      modifiedOn: 1690564105133,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105133",
      quantity: 500,
      rate: 468.89,
      remaining: 500,
      stockType: "stocks",
      ticker: "NVDA",
      type: "buy",
      undefined: null,
    },
    {
      _id: "1690564105147",
      companyName: "Meta Platforms, Inc. Class A Common Stock",
      createdOn: 1690564105147,
      date: "2023-07-19",
      logoUrl: "https://media.traderverse.io/assets/equity/META_NASDAQ.svg",
      modifiedBy: "",
      modifiedOn: 1690564350449,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105147",
      quantity: 3000,
      rate: 324.64,
      remaining: 1500,
      stockType: "stocks",
      ticker: "META",
      type: "buy",
      undefined: null,
    },
    {
      _id: "1690564105160",
      companyName: "Tether",
      createdOn: 1690564105160,
      date: "2023-07-28",
      logoUrl: "https://media.traderverse.io/assets/crypto/USDT_CRYPTO.svg",
      modifiedBy: "",
      modifiedOn: 1690564350387,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105160",
      quantity: 2000,
      rate: 1,
      remaining: 1000,
      stockType: "crypto",
      ticker: "USDT",
      type: "buy",
      undefined: null,
    },
    {
      _id: "1690564105175",
      companyName: "TESLA INC",
      createdOn: 1690564105175,
      date: "2023-07-27",
      logoUrl: "https://media.traderverse.io/assets/equity/TSLA_XNGS.svg",
      modifiedBy: "",
      modifiedOn: 1690564350374,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105175",
      quantity: 1500,
      rate: 265.9,
      remaining: 1000,
      stockType: "stocks",
      ticker: "TSLA",
      type: "buy",
      undefined: null,
    },
    {
      _id: "1690564350362",
      buyId: "1690564105175",
      companyName: "TESLA INC",
      createdOn: 1690564350362,
      date: "2023-07-27",
      logoUrl: "https://media.traderverse.io/assets/equity/TSLA_XNGS.svg",
      modifiedBy: "",
      modifiedOn: 1690564350363,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105175",
      quantity: 500,
      rate: 267.9,
      remaining: 500,
      stockType: "stocks",
      ticker: "TSLA",
      type: "sell",
    },
    {
      _id: "1690564350380",
      buyId: "1690564105160",
      companyName: "Tether",
      createdOn: 1690564350380,
      date: "2023-07-28",
      logoUrl: "https://media.traderverse.io/assets/crypto/USDT_CRYPTO.svg",
      modifiedBy: "",
      modifiedOn: 1690564350381,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105160",
      quantity: 1000,
      rate: 1.5,
      remaining: 1000,
      stockType: "crypto",
      ticker: "USDT",
      type: "sell",
    },
    {
      _id: "1690564350397",
      buyId: "1690564105147",
      companyName: "Meta Platforms, Inc. Class A Common Stock",
      createdOn: 1690564350397,
      date: "2023-07-25",
      logoUrl: "https://media.traderverse.io/assets/equity/META_NASDAQ.svg",
      modifiedBy: "",
      modifiedOn: 1690564350397,
      portfolioId: "1690562028870",
      portfolioItemid: "1690564105147",
      quantity: 1500,
      rate: 323.66,
      remaining: 1500,
      stockType: "stocks",
      ticker: "META",
      type: "sell",
    },
  ];

  dummyLineItems: any[] = [
    {
      _id: "1690560117756",
      createdOn: 1690560117779,
      profit: 0,
      quantity: 100,
      rate: 132.205,
      ticker: "AMZN",
      costPrice: 132.205,
      remaining: 100,
      sellPrice: 0,
      companyName: "Amazon.com, Inc. Common Stock",
      logoUrl: "https://media.traderverse.io/assets/equity/AMZN_NASDAQ.svg",
      stockType: "stocks",
      marketCap: 1321539600384,
      change: -0.9230746,
      originalPrice: 128.8,
    },
    {
      _id: "1690560117786",
      createdOn: 1690560117790,
      profit: 0,
      quantity: 50,
      rate: 196.23,
      ticker: "AAPL",
      costPrice: 196.23,
      remaining: 50,
      sellPrice: 0,
      companyName: "Apple Inc. Common Stock",
      logoUrl: "https://media.traderverse.io/assets/equity/AAPL_NASDAQ.svg",
      stockType: "stocks",
      marketCap: 3031706894336,
      change: 0.4220056,
      originalPrice: 192.75,
    },
    {
      _id: "1690560117798",
      createdOn: 1690560117803,
      profit: 0,
      quantity: 150,
      rate: 133.55,
      ticker: "GOOGL",
      costPrice: 133.55,
      remaining: 150,
      sellPrice: 0,
      companyName: "ALPHABET INC",
      logoUrl: "https://media.traderverse.io/assets/equity/GOOGL_XNGS.svg",
      stockType: "stocks",
      marketCap: 1274837729280,
      change: -7.683014,
      originalPrice: 99.37,
    },
    {
      _id: "1690560117809",
      createdOn: 1690560117814,
      profit: 0,
      quantity: 100,
      rate: 324.59,
      ticker: "META",
      costPrice: 324.59,
      remaining: 100,
      sellPrice: 0,
      companyName: "Meta Platforms, Inc. Class A Common Stock",
      logoUrl: "https://media.traderverse.io/assets/equity/META_NASDAQ.svg",
      stockType: "stocks",
      marketCap: 747317624832,
      change: -0.90057236,
      originalPrice: 291.61,
    },
    {
      _id: "1690560117820",
      createdOn: 1690560117825,
      profit: 0,
      quantity: 200,
      rate: 264.58,
      ticker: "TSLA",
      costPrice: 264.58,
      remaining: 200,
      sellPrice: 0,
      companyName: "TESLA INC",
      logoUrl: "https://media.traderverse.io/assets/equity/TSLA_XNGS.svg",
      stockType: "stocks",
      marketCap: 636901654528,
      change: 2.276305,
      originalPrice: 201.29,
    },
    {
      _id: "1690560117838",
      createdOn: 1690560117844,
      profit: 0,
      quantity: 150,
      rate: 337.4339,
      ticker: "MSFT",
      costPrice: 337.4339,
      remaining: 150,
      sellPrice: 0,
      companyName: "Microsoft Corporation Common Stock",
      logoUrl: "https://media.traderverse.io/assets/equity/MSFT_NASDAQ.svg",
      stockType: "stocks",
      marketCap: 2566061817856,
      change: 0.38979444,
      originalPrice: 345.11,
    },
    {
      _id: "1690560117856",
      createdOn: 1690560840727,
      profit: -827.5999999999985,
      quantity: 50,
      rate: 469.39,
      ticker: "NVDA",
      costPrice: 469.39,
      remaining: 50,
      sellPrice: 452.838,
      companyName: "NVIDIA Corporation Common Stock",
      logoUrl: "https://media.traderverse.io/assets/equity/NVDA_NASDAQ.svg",
      stockType: "stocks",
    },
    {
      _id: "1690560536028",
      createdOn: 1690560840704,
      profit: 38295.79999999993,
      quantity: 30,
      rate: 29368.59,
      ticker: "BTC",
      costPrice: 29368.59,
      remaining: 30,
      sellPrice: 31283.379999999997,
      companyName: "Bitcoin",
      logoUrl: "https://media.traderverse.io/assets/crypto/BTC_CRYPTO.svg",
      stockType: "crypto",
    },
    {
      _id: "1690560536054",
      createdOn: 1690560840668,
      profit: -10000,
      quantity: 50,
      rate: 1874.28,
      ticker: "ETH",
      costPrice: 1874.28,
      remaining: 50,
      sellPrice: 1674.28,
      companyName: "Ethereum",
      logoUrl: "https://media.traderverse.io/assets/crypto/ETH_CRYPTO.svg",
      stockType: "crypto",
    },
    {
      _id: "1690560536067",
      createdOn: 1690560840637,
      profit: 2000.2,
      quantity: 2000,
      rate: 0.9999,
      ticker: "USDT",
      costPrice: 0.9999,
      remaining: 2000,
      sellPrice: 2,
      companyName: "Tether",
      logoUrl: "https://media.traderverse.io/assets/crypto/USDT_CRYPTO.svg",
      stockType: "crypto",
    },
    {
      _id: "1690560536079",
      createdOn: 1690560840603,
      profit: 500,
      quantity: 1000,
      rate: 1,
      ticker: "USDCE",
      costPrice: 1,
      remaining: 1000,
      sellPrice: 1.5,
      companyName: "USD Coin Bridged",
      logoUrl: "https://media.traderverse.io/assets/crypto/USDCE_CRYPTO.svg",
      stockType: "crypto",
    },
  ];

  attributesList: any[] = [
    {
      name: "Founder",
      color: "#5000ff",
      label: "common.common.founder",
    },
    {
      name: "Fundamental Trading",
      color: "#FDF621",
      label: "common.common.fundamental_trading",
    },
    {
      name: "Fundamental Trader",
      color: "#FDF621",
      label: "common.common.fundamental_trading",
    },
    {
      name: "Technical Trading",
      color: "#43FF6E",
      label: "common.common.technical_trading",
    },
    {
      name: "Scalping",
      color: "#FF895A",
      label: "common.common.scalping",
    },
    {
      name: "Position Trading",
      color: "#6FFCD0",
      label: "common.common.position_trading",
    },
    {
      name: "Arbitrage Trading",
      color: "#B87BFF",
      label: "common.common.arbitrage_trading",
    },
    {
      name: "Noise Trading",
      color: "rgb(235 160 16)",
      label: "common.common.noise_trading",
    },
    {
      name: "Sentiment Trading",
      color: "#4BB4FF",
      label: "common.common.sentiment_trading",
    },
    {
      name: "Price Action Trading",
      color: "#FC8383",
      label: "common.common.price_action_trading",
    },
    {
      name: "Day Trading",
      color: "#FF71CD",
      label: "common.common.day_trading",
    },
    {
      name: "Swing Trading",
      color: "#FF3B3B",
      label: "common.common.swing_trading",
    },
    {
      name: "Contrarian Trading",
      color: "#DCBEFF",
      label: "common.common.contrarian_trading",
    },
    {
      name: "Market Timing",
      color: "#00D068",
      label: "common.common.market_timing",
    },
    {
      name: "Algorithmic Trading",
      color: "#00BCD4",
      label: "common.common.algorithmic_trading",
    },
    {
      name: "Momentum Trading",
      color: "rgb(152 138 111)",
      label: "common.common.momentum_trading",
    },
  ];
  getLabel(item: any) {
    return (
      this.attributesList.find((attribute) => attribute.name == item.name)
        ?.label ?? item.name
    );
  }
  portfolioAccounts: any[] = [
    {
      directory: "portfolio_wallets",
      type: "Exchanges",
      image: "binance.png",
      title: "My Portfolio 1",
      category: "Binance",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#C48D05",
    },
    {
      directory: "portfolio_wallets",
      type: "Exchanges",
      image: "gate.png",
      title: "My Portfolio 2",
      category: "Gate.io",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#25C791",
    },
    {
      directory: "portfolio_wallets",
      type: "Exchanges",
      image: "crypto.png",
      title: "My Portfolio 3",
      category: "Crypto",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#03316C",
    },
    {
      directory: "portfolio_wallets",
      type: "Wallets",
      image: "metamask.png",
      title: "My Portfolio 4",
      category: "Metamask",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#E4761B",
    },
    {
      directory: "portfolio_wallets",
      type: "Exchanges",
      image: "kraken.png",
      title: "My Portfolio 5",
      category: "Kraken",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#5741D9",
    },
    {
      directory: "portfolio_wallets",
      type: "Exchanges",
      image: "blockfi.png",
      title: "My Portfolio 6",
      category: "BlockFi",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#004BEF",
    },
    {
      directory: "portfolio_wallets",
      type: "Exchanges",
      image: "exodus.png",
      title: "My Portfolio 7",
      category: "Exodus",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#8B63FC",
    },
    {
      directory: "portfolio_wallets",
      type: "Exchanges",
      image: "gemini.png",
      title: "My Portfolio 8",
      category: "Gemini",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#17BBD5",
    },
    {
      directory: "portfolio_wallets",
      type: "Wallets",
      image: "coinbase.png",
      title: "My Portfolio 9",
      category: "Coinbase ",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#0052FF",
    },
    {
      directory: "portfolio_wallets",
      type: "Wallets",
      image: "nanoledger.png",
      title: "My Portfolio 10",
      category: "Nano Ledger",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "var(--card-inner-color-profile)",
    },
    {
      directory: "portfolio_wallets",
      type: "Exchanges",
      image: "ftx.png",
      title: "My Portfolio 11",
      category: "FTX.US",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#1297B2",
    },
    {
      directory: "portfolio_wallets",
      type: "Wallets",
      image: "electrum.png",
      title: "My Portfolio 12",
      category: "Electrum",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#0056C0",
    },
    {
      directory: "portfolio_wallets",
      type: "Wallets",
      image: "trezon.png",
      title: "My Portfolio 13",
      category: "Trazor",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "var(--card-inner-color-profile)",
    },
    {
      directory: "portfolio_wallets",
      type: "Wallets",
      image: "trustwallet.png",
      title: "My Portfolio 14",
      category: "Trust Wallet",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#3375BB",
    },
    {
      directory: "portfolio_wallets",
      type: "Wallets",
      image: "hyperledger.png",
      title: "My Portfolio 14",
      category: "HyperLedger ",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#2F3135",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "etrade.png",
      title: "ETrade",
      category: "Etrade",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#99b138",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "ally.png",
      title: "My Portfolio 1",
      category: "Ally Invest",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#651061",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "ameri.png",
      title: "My Portfolio 2",
      category: "Ameritrade",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#0c4636",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "charles.png",
      title: "My Portfolio 3",
      category: "Charles Schwab",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#0d9ed7",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "chase.png",
      title: "My Portfolio 4",
      category: "Chase",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#177cbc",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "fidelity.png",
      title: "My Portfolio 5",
      category: "Fidelity",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#d38c0d",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "firsttrader.png",
      title: "My Portfolio 6",
      category: "First Trader",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#33578e",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "interactive.png",
      title: "My Portfolio 7",
      category: "Interactive Broker",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#ac1a27",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "merilllynch.png",
      title: "My Portfolio 8",
      category: "Merrill Lynch",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#0e256c",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "robinhood.png",
      title: "My Portfolio 9",
      category: "Robinhood",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#479f49",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "tradestation.png",
      title: "My Portfolio 10",
      category: "Tradestation",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#1eada1",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "vanguard.png",
      title: "My Portfolio 11",
      category: "Vanguard",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#962027",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "webull.png",
      title: "My Portfolio 12",
      category: "Webull",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#0c4adf",
    },
    {
      directory: "portfolio_brokerage",
      type: "Brokerage",
      image: "wells.png",
      title: "My Portfolio 13",
      category: "Wells Fargo",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#d12832",
    },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "binance.png",
    //   title: "My Portfolio 1",
    //   category: "Binance",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#C48D05",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "gate.png",
    //   title: "My Portfolio 2",
    //   category: "Gate.io",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#25C791",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "crypto.png",
    //   title: "My Portfolio 3",
    //   category: "Crypto",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#03316C",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "metamask.png",
    //   title: "My Portfolio 4",
    //   category: "Metamask",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#E4761B",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "kraken.png",
    //   title: "My Portfolio 5",
    //   category: "Kraken",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#5741D9",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "blockfi.png",
    //   title: "My Portfolio 6",
    //   category: "BlockFi",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#004BEF",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "exodus.png",
    //   title: "My Portfolio 7",
    //   category: "Exodus",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#8B63FC",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "gemini.png",
    //   title: "My Portfolio 8",
    //   category: "Gemini",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#17BBD5",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "coinbase.png",
    //   title: "My Portfolio 9",
    //   category: "Coinbase ",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#0052FF",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "nanoledger.png",
    //   title: "My Portfolio 10",
    //   category: "Nano Ledger",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "var(--card-inner-color-profile)",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "ftx.png",
    //   title: "My Portfolio 11",
    //   category: "FTX.US",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#1297B2",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "electrum.png",
    //   title: "My Portfolio 12",
    //   category: "Electrum",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#0056C0",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "trezon.png",
    //   title: "My Portfolio 13",
    //   category: "Trezon",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "var(--card-inner-color-profile)",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "trustwallet.png",
    //   title: "My Portfolio 14",
    //   category: "Trust Wallet",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#3375BB",
    // },
    // {
    //   directory: "portfolio_wallets",
    //   type: "Wallets",
    //   image: "hyperledger.png",
    //   title: "My Portfolio 14",
    //   category: "HyperLedger ",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#2F3135",
    // },
    {
      directory: "portfolio_payment",
      image: "amazonpay.png",
      title: "Simon Lewis",
      type: "Payment",
      category: "Amazon Pay",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: false,
      color: "#DD8D00",
    },
    {
      directory: "portfolio_payment",
      image: "stripe.png",
      title: "Simon Lewis",
      category: "Stripe",
      balance: 73777.99,
      type: "Payment",
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#6760f5",
    },
    {
      directory: "portfolio_payment",
      image: "block.png",
      title: "Simon Lewis",
      category: "Block",
      balance: 73777.99,
      stocks: 0,
      type: "Payment",
      crypto: 0,
      isConnected: true,
      color: "#14AFA3",
    },
    {
      directory: "portfolio_payment",
      image: "paypal.png",
      title: "Simon Lewis",
      category: "PayPal",
      balance: 73777.99,
      type: "Payment",
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#003085",
    },
    {
      directory: "portfolio_payment",
      type: "Payment",
      image: "apple.png",
      title: "Simon Lewis",
      category: "Apple Pay",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "var(--card-inner-color-profile)",
    },
    {
      directory: "portfolio_payment",
      type: "Payment",
      image: "googlewallet.png",
      title: "Simon Lewis",
      category: "Google Wallet",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#f5342e",
    },
    {
      directory: "portfolio_payment",
      type: "Payment",
      image: "coingate.png",
      title: "Simon Lewis",
      category: "Coin Gate",
      balance: 73777.99,
      stocks: 0,
      crypto: 0,
      isConnected: true,
      color: "#562ce6",
    },
    // {
    //   directory: "portfolio_accounts",
    //   image: "banking-1.png",
    //   title: "Fidelity",
    //   type: "Banking",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: false,
    //   color: "#0a4c90",
    // },
    // {
    //   directory: "portfolio_accounts",
    //   image: "banking-2.png",
    //   title: "Chase",
    //   type: "Banking",
    //   balance: 73777.99,
    //   color: "#0c66b5",
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    // },
    // {
    //   directory: "portfolio_accounts",
    //   image: "banking-3.png",
    //   type: "Banking",
    //   title: "Ameritrade",
    //   balance: 73777.99,
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#f1832f",
    // },
    // {
    //   directory: "portfolio_accounts",
    //   image: "banking-4.png",
    //   title: "Chase",
    //   balance: 73777.99,
    //   type: "Banking",
    //   stocks: 0,
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#481f82",
    // },
    // {
    //   directory: "portfolio_accounts",
    //   image: "banking-5.png",
    //   title: "Fidelity",
    //   balance: 73777.99,
    //   stocks: 0,
    //   type: "Banking",
    //   crypto: 0,
    //   isConnected: true,
    //   color: "#d52630",
    // },
  ];

  StatsList = [
    {
      label: "Followersss",
      value: 0,
      key: "followersCount",
      icon: "icon-followers",
      color: "#ff851b",
    },
    {
      label: "Following",
      value: 0,
      key: "followingCount",
      icon: "icon-following",
      color: "#21c1fd",
    },
    {
      label: "Friends",
      value: 0,
      key: "friendCount",
      icon: "icon-friends",
      color: "#cbff18",
    },
    {
      label: "Investment #",
      value: 0,
      key: "investmentsCount",
      icon: "icon-investment",
      color: "#598aff",
    },
    {
      label: "Investment $",
      value: 0,
      key: "investments",
      icon: "icon-price",
      color: "#ff6ea3",
    },
    {
      label: "# Of Trades",
      value: 0,
      key: "trades",
      icon: "icon-trade",
      color: "#fcf6cb",
    },
    {
      label: "Trades $",
      value: 0,
      key: "tradesPrice",
      icon: "icon-trades-dollor",
      color: "#DCBEFF",
    },
    {
      label: "Avg Gain $",
      value: 0,
      key: "avgGainPrice",
      icon: "icon-price",
      color: "#39d3d3",
    },
    {
      label: "Avg % Gain",
      value: 0,
      key: "avgGain",
      icon: "icon-count",
      color: "#34E36F",
    },
    {
      label: "Top Gain $",
      value: 0,
      key: "topGainPrice",
      icon: "icon-top-gain-dollor",
      color: "#6FFCD0",
    },
    {
      label: "Top % Gain",
      value: 0,
      key: "topGain",
      icon: "icon-count",
      color: "#f2aa1f",
    },
    {
      label: "Avg TR/Month",
      value: 0,
      key: "avgTradesPerMonth",
      icon: "icon-monthly-trade",
      color: "#34E36F",
    },
    {
      label: "Avg TR/Week",
      value: 0,
      key: "avgTradesPerWeek",
      icon: "icon-week-trade",
      color: "#934BFF",
    },
    {
      label: "Views",
      value: 0,
      key: "viewsCount",
      icon: "icon-view",
      color: "#00bcd4",
    },
    {
      label: "Outreach",
      value: 0,
      key: "outreach",
      icon: "icon-outreach",
      color: "#009e3a",
    },

    // Below are entries not covered in your original function; left icon/color as placeholders
    {
      label: "Monthly Trades",
      value: 0,
      key: "tradesThisMonth",
      icon: "icon-monthly-trade",
      color: "#34E36F",
    },
    {
      label: "Weekly Trades",
      value: 0,
      key: "tradesThisWeek",
      icon: "icon-week-trade",
      color: "#934BFF",
    },
    {
      label: "Yearly Trades",
      value: 0,
      key: "tradesThisYear",
      icon: "icon-yearly-trades",
      color: "#8EC001",
    },
    {
      label: "Daily Trades",
      value: 0,
      key: "todayTrades",
      icon: "icon-calendar",
      color: "#6600ff",
    },
    {
      label: "Interests",
      value: 0,
      key: "traderTypeCount",
      icon: "icon-trader-type",
      color: "#F2AB3C",
    },
    {
      label: "Best Trades",
      value: 0,
      key: "bestTradeCount",
      icon: "",
      color: "",
    }, ////////
    {
      label: "Favorite Platforms",
      value: 0,
      key: "favoritePlatformCount",
      icon: "icon-favorite-platform",
      color: "#D33F6F",
    },
    {
      label: "Recommendations",
      value: 0,
      key: "recommendationCount",
      icon: "icon-recommendation",
      color: "#598AFF",
    },
    {
      label: "Watchlists",
      value: 0,
      key: "watchListCount",
      icon: "icon-watchlist",
      color: "#85A335",
    },
    { label: "Lists", value: 0, key: "listCount", icon: "", color: "" }, ////////
    {
      label: "Links",
      value: 0,
      key: "linksCount",
      icon: "icon-link",
      color: "#EB4F41",
    },
    { label: "Images", value: 0, key: "imagesCount", icon: "", color: "" }, //////
    {
      label: "Favorite Influencers",
      value: 0,
      key: "favoriteInfluencerCount",
      icon: "icon-favorite-influencers",
      color: "#8B4AF1",
    },
    {
      label: "Favorite Investments",
      value: 0,
      key: "favoriteInvestmentCount",
      icon: "icon-favorite-investment",
      color: "#EE8131",
    },
    {
      label: "Playbooks",
      value: 0,
      key: "playbookCount",
      icon: "icon-playbook",
      color: "#E9E582",
    },
    {
      label: "Profit And Loss",
      value: 0,
      key: "profitAndLossCount",
      icon: "icon-profit-loss",
      color: "#73E5EB",
    },
    {
      label: "Predictions",
      value: 0,
      key: "predictionCount",
      icon: "icon-prediction",
      color: "#F89272",
    },
    {
      label: "Social Links",
      value: 0,
      key: "socialLnksCount",
      icon: "icon-link",
      color: "#EB4F41",
    },
  ];

  widgetsWidth: any = {
    flex: "0 0 433px",
    maxWidth: "433px",
  };

  getStatLabel(stat: any) {
    if (!stat.key) {
      stat.key = this.getStatKey(stat.label);
    }
    var ind = this.StatsList.findIndex((x) => x.key == stat.key);
    if (ind == -1) {
      return stat.label;
    } else {
      return this.StatsList[ind].label;
    }
  }

  getStatKey(label: string) {
    if (label == "Followers") {
      return "followersCount";
    } else if (label == "Following") {
      return "followingCount";
    } else if (label == "Friends") {
      return "friendCount";
    } else if (label == "Investment #") {
      return "investmentsCount";
    } else if (label == "Investment $" || label == "Investments") {
      return "investments";
    } else if (label == "Trades #" || label == "Invested") {
      return "trades";
    } else if (label == "Trades $") {
      return "tradesPrice";
    } else if (label == "Avg Gain $") {
      return "avgGainPrice";
    } else if (
      label == "Avg Gain %" ||
      label == "Avg % Gain" ||
      label == "Avg Gains"
    ) {
      return "avgGain";
    } else if (label == "Top Gain $") {
      return "topGainPrice";
    } else if (
      label == "Top Gain %" ||
      label == "Top % Gain" ||
      label == "Top Gains"
    ) {
      return "topGain";
    } else if (label == "TR/Month" || label == "Avg TR/Month") {
      return "avgTradesPerMonth";
    } else if (label == "TR/Week" || label == "Avg TR/Week") {
      return "avgTradesPerWeek";
    } else if (label == "Views") {
      return "viewsCount";
    } else if (label == "Today Trades" || label == "Daily Trades") {
      return "todayTrades";
    } else if (label == "Trades This Month" || label == "Monthly Trades") {
      return "tradesThisMonth";
    } else if (label == "Trades This Week" || label == "Weekly Trades") {
      return "tradesThisWeek";
    } else if (label == "Trades This Year" || label == "Yearly Trades") {
      return "tradesThisYear";
    } else if (label == "Outreach") {
      return "outreach";
    } else if (label == "Trader Type") {
      return "traderTypeCount";
    } else if (label == "Best Trades") {
      return "bestTradeCount";
    } else if (label == "Favorite Platforms") {
      return "favoritePlatformCount";
    } else if (label == "Recommendations") {
      return "recommendationCount";
    } else if (label == "Watchlists") {
      return "watchListCount";
    } else if (label == "Lists") {
      return "listCount";
    } else if (label == "Links") {
      return "linksCount";
    } else if (label == "Images") {
      return "imagesCount";
    } else if (label == "Favorite Influencers") {
      return "favoriteInfluencerCount";
    } else if (label == "Favorite Investments") {
      return "favoriteInvestmentCount";
    } else if (label == "Playbooks") {
      return "playbookCount";
    } else if (label == "Profit And Loss") {
      return "profitAndLossCount";
    } else if (label == "Predictions") {
      return "predictionCount";
    } else if (label == "Social Links") {
      return "socialLnksCount";
    }
  }

  hexToRgb(hex: string) {
    // Remove the leading # if present
    hex = hex.replace(/^#/, "");

    // Convert shorthand hex color to full format
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map(function (s) {
          return s + s;
        })
        .join("");
    }

    // Parse hexadecimal color to RGB
    var bigint = parseInt(hex, 16);
    var r = (bigint >> 16) & 255;
    var g = (bigint >> 8) & 255;
    var b = bigint & 255;

    return `${r}, ${g}, ${b}`;
  }

  onModalOpen(component: any, data: any = {}) {
    return new Promise<any>(async (resolve, reject) => {
      // this.disableScroll();
      var modal = await openModal(component, data);
      modal.onclose = () => {
        // this.enableScroll();
      };
      resolve(modal);
    });
  }
  scrollTop: number = 0;

  enableScroll() {
    var el: any = document.querySelector("#app");
    document.body.style.overflow = "auto";
    if (el) {
      el.style.overflow = "visible";
    }
    if (this.scrollTop > 0) {
      document.body.scrollTo(0, this.scrollTop);
      el.scrollTo(0, this.scrollTop);
    }
  }

  disableScroll() {
    this.scrollTop =
      document.documentElement.scrollTop || document.body.scrollTop;
    document.body.style.overflow = "hidden";
    var el: any = document.querySelector("#app");
    if (el) {
      el.style.overflow = "hidden";
    }
  }

  removeElementsByClassName(className: string) {
    var elements: any = document.getElementsByClassName(className);
    if (elements && elements.length) {
      var elementToRemove: any = elements[elements.length - 1];
      elementToRemove.parentNode.removeChild(elementToRemove);
    }
  }
  removeElementById(id: string) {
    var elementToRemove: any = document.getElementById(id);
    if (elementToRemove) {
      elementToRemove.parentNode.removeChild(elementToRemove);
    }
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  calculateDateRange(period: string) {
    const endDate = new Date();
    const startDate = new Date(endDate);

    switch (period) {
      case "1D":
        startDate.setDate(startDate.getDate() - 1);
        break;
      case "5D":
        startDate.setDate(startDate.getDate() - 5);
        break;
      case "1M":
        startDate.setDate(startDate.getMonth() - 1);
        break;
      case "3M":
        startDate.setMonth(startDate.getMonth() - 3);
        break;
      case "6M":
        startDate.setMonth(startDate.getMonth() - 6);
        break;
      case "1Y":
        startDate.setMonth(startDate.getMonth() - 12);
        break;
      case "2Y":
        startDate.setFullYear(startDate.getFullYear() - 2);
        break;
      default:
        startDate.setDate(startDate.getDate() - 1);
        console.warn(
          `Unknown period '${period}' provided. Defaulting to 1 day lookback.`
        );
    }

    return {
      start_date: this.formatDate(startDate),
      end_date: this.formatDate(endDate),
    };
  }

  getExecutiveImage = (isMr: boolean) => {
    var assetName = "dummy_ms";
    if (isMr) {
      assetName = "dummy_mr";
    }
    return `https://imagedelivery.net/09I_Y_JIwzvjUiGrV3dv3Q/images/${assetName}.png?alt=media`;
    // return `https://firebasestorage.googleapis.com/v0/b/${this.FirebaseConfig.storageBucket}/o/assets%2F${assetName}.png?alt=media`;
  };

  // Feed Work //

  public margin: number = 25;
  private readCount: number = 0;
  private initialColWidth: number = 450;
  widthOfCol: number = 450;

  calculateConfigurationFromFeed(
    elemId: string,
    configuration: ConfigurationModel = new ConfigurationModel(),
    isShimmerEnable: boolean = false,
    isUpdate: boolean = false
  ) {
    var el: any = document.getElementById(elemId);
    if (window.innerWidth < 1600) {
      this.widthOfCol = 360;
      this.margin = 17;
    } else {
      this.widthOfCol = 450;
      this.margin = 25;
    }
    if (el) {
      const className = "lay_col";
      if (el.clientWidth < 768) {
        el.classList.add(className);
        configuration = new ConfigurationModel(1, 1, "100%");
        isShimmerEnable = false;
      } else {
        var frameWidth = el.offsetWidth;
        // if (frameWidth <= 1200 + this.margin) {
        //   configuration = new ConfigurationModel(1, 1, "100%");
        //   el.classList.add(className);
        // } else {
        // configuration.widgetsCount = 0;
        el.classList.remove(className);

        configuration = this.checkWidth(
          configuration,
          isShimmerEnable,
          frameWidth,
          this.margin,
          isUpdate
        );
        // }
      }
    }
    if (
      configuration.widgetsWidth == "0px" ||
      configuration.activitiesWidth == "0px"
    ) {
      configuration = this.calculateConfigurationFromFeed(
        elemId,
        configuration,
        isShimmerEnable
      );
    }
    return configuration;
  }

  checkWidth(
    configuration: ConfigurationModel,
    isShimmerEnable: boolean,
    screenWidth: number,
    margin: number,
    isUpdate: boolean = false
  ): ConfigurationModel {
    var activitiesCount = configuration.activitiesCount!;
    var widgetsCount = configuration.widgetsCount!;
    var showWidgets = configuration.showWidgets ?? true;
    var showTimeline = configuration.showTimeline;
    var isChange: boolean = false;
    // var borderWidth = 6;
    // if (screenWidth < 2000) {
    //   this.widthOfCol = 350
    // }
    // if (screenWidth < 1600) {
    //   this.widthOfCol = 300
    // }
    // if (screenWidth < 1400) {
    //   this.widthOfCol = 200
    // }
    // if(screenWidth >= 2000 && screenWidth < 1260) {
    //   this.widthOfCol = 450
    // }
    var widgetsSectionWidth =
      margin * 2 +
      widgetsCount * this.widthOfCol +
      margin * (widgetsCount - 1) -
      10;
    // widgetsCount * this.widthOfCol  + (margin * (widgetsCount - 1));
    // +
    // borderWidth * widgetsCount;
    var activitiesSectionWidth =
      activitiesCount * this.widthOfCol +
      margin * configuration.activitiesCount -
      margin;
    var screenUsage = activitiesSectionWidth + widgetsSectionWidth;
    if (screenWidth < screenUsage) {
      if (activitiesCount > 1) {
        activitiesCount--;
        isChange = true;
      } else if (widgetsCount > 1) {
        widgetsCount--;
        isChange = true;
      }
    }
    var totalColumns = activitiesCount + widgetsCount;
    var calcMaxWidth = screenWidth / totalColumns - (totalColumns - 2) * margin;
    if (calcMaxWidth > 700) {
      calcMaxWidth = 700;
    }
    var maxWidth = `${calcMaxWidth}px`;
    configuration = new ConfigurationModel(
      activitiesCount,
      widgetsCount,
      maxWidth,
      totalColumns,
      showWidgets,
      showTimeline
    );
    if (isChange) {
      return this.checkWidth(
        configuration,
        isShimmerEnable,
        screenWidth,
        margin
      );
    } else {
      activitiesSectionWidth = screenWidth - widgetsSectionWidth - margin * 2;
      if (activitiesSectionWidth < 700 && widgetsCount > 1) {
        widgetsCount--;
        activitiesCount++;
      }
      var widgetsExtraWidth = 14;
      widgetsSectionWidth =
        margin * 2 +
        widgetsCount * this.widthOfCol +
        margin * (widgetsCount - 1) +
        widgetsExtraWidth;
      // widgetsSectionWidth =
      //   widgetsCount * this.widthOfCol + widgetsExtraWidth + margin * widgetsCount + 6;
      activitiesSectionWidth = screenWidth - widgetsSectionWidth - margin * 2;
      // if (isUpdate || screenWidth >= 2000 || screenWidth < 1260) {
      configuration.widgetsCount = widgetsCount;
      configuration.activitiesCount = activitiesCount;
      configuration.totalColumns = widgetsCount + activitiesCount;
      // } else {
      // configuration.widgetsCount = 1;
      // configuration.activitiesCount = 3;
      // configuration.totalColumns = 4;
      // }

      // when widget is hidden the total count is available for timeline posts
      if (!showWidgets) {
        configuration.activitiesCount = configuration.totalColumns;
        configuration.widgetsCount = 0;
      }
      configuration.widgetsWidth = widgetsSectionWidth + "px";
      configuration.activitiesWidth = activitiesSectionWidth + "px";
      configuration.showWidgets = showWidgets;
      configuration.showTimeline = showTimeline;
      if (isShimmerEnable == true) {
        setTimeout(() => {
          isShimmerEnable = false;
        }, 2500);
      }
      return configuration;
    }
  }
  isSmallScreen() {
    if (!window) return false;
    let isSmall = window.innerWidth < 1600;
    const handleResize = () => {
      isSmall = window.innerWidth < 1600;
    };
    window.addEventListener("resize", handleResize);
    return isSmall;
  }

  getStockCDNImage = (
    logoUrl: string,
    stockId: string,
    type: StockMarkets | string = StockMarkets.stocks
  ) => {
    if (logoUrl && logoUrl.includes("media.traderverse.io")) {
      return logoUrl;
    }
    if (stockId) {
      const raw = String(stockId).trim().toUpperCase().replace(/\s+/g, "");
      const isCrypto =
        type == StockMarkets.crypto ||
        String(type).toLowerCase() === "crypto" ||
        raw.endsWith("_CRYPTO") ||
        /-USD$/i.test(raw);

      if (isCrypto) {
        const symbol = raw
          .replace(/_CRYPTO$/i, "")
          .replace(/-USD$/i, "")
          .split("_")[0]
          .split("-")[0];
        return `https://media.traderverse.io/assets/crypto/${symbol}_CRYPTO.svg`;
      }

      const exchanges = new Set([
        "NASDAQ",
        "NYSE",
        "AMEX",
        "XNGS",
        "ARCA",
        "BATS",
        "OTC",
        "CBOE",
        "IEX",
      ]);
      if (raw.includes("_")) {
        const last = raw.split("_").pop()!;
        if (exchanges.has(last)) {
          return `https://media.traderverse.io/assets/equity/${raw}.svg`;
        }
      }
      const symbol = raw.split("_")[0].split("-")[0] || raw;
      return `https://media.traderverse.io/assets/equity/${symbol}_NASDAQ.svg`;
    }
    if (type == StockMarkets.stocks) {
      return `https://cdn-images.traderverse.io/stocks_dummy.svg`;
    }
    return `https://cdn-images.traderverse.io/crypto_dummy.svg`;
  };

  chartTheme: any = {
    light: {
      "--text-alt-color": "#999b9f",
      "--card-inner-color-profile": "#eaeaef",
      "--chart-legend": "#1d2733",
      "--card-background-color": "#fff",
      "--card-tile-color": "#fff",
      "--text-color": "#1d2733",
    },
    default: {
      "--text-alt-color": "#5f4c9c",
      "--card-inner-color-profile": "#181326",
      "--chart-legend": "#ffffff4d",
      "--card-background-color": "#1e1a30",
      "--card-tile-color": "#25203c",
      "--text-color": "#fff",
    },
  };

  getStockCryptoByKeyword = async (keyword: string) => {
    const { searchStocks } = useStockSocket();
    if (keyword.length) {
      const response: any = await searchStocks(keyword);
      if (response && response.length) {
        return response.map((x: any) => {
          var user = new GeneralStockModel();
          user = Object.assign(user, x);
          return user;
        });
      }
    }
    return [];
  };

  getUserByKeyword = async (keyword: string) => {
    if (keyword.length) {
      const response: any = await httpService.getUsers(keyword);
      if (
        response.data.isSuccess &&
        response.data.data &&
        response.data.data.length
      ) {
        return response.data.data.map((x: any) => {
          var user = new GeneralUserModel();
          user = Object.assign(user, x);
          return user;
        });
      }
    }
    return [];
  };

  updateReadCount() {
    this.readCount = storageService.GetProperty("readCount");
    if (!this.readCount) {
      this.readCount = 0;
    }
    this.readCount++;
    storageService.SetProperty("readCount", this.readCount);
  }

  appCategories = [
    { label: "Nasdaq", value: "nasdaq" },
    { label: "OTC", value: "otc" },
    { label: "Crypto", value: "crypto" },
    { label: "Stocks", value: "stocks" },
    { label: "Forex", value: "forex" },
    { label: "NFTs", value: "nft" },
  ];

  getColumnWidth(area: any, count: number): Promise<string> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (area != null) {
          var areaWidth = area["clientWidth"];
          var postTotalMargin = this.margin * (count - 1);
          var maxWidth = (areaWidth - postTotalMargin) / count;
          if (maxWidth > 700) {
            maxWidth = 700;
          }
          resolve(`${maxWidth}px`);
        }
        resolve("600px");
      }, 1);
    });
  }

  getUserFromPost = (post: PostsModel) => {
    var user = new User();
    if (post) {
      user._id = post.userId;
      user.fullName = post.fullName;
      user.country = post.userCountry;
      user.userName = post.userName;
      user.profilePicture = this.getUserProfilePicture(
        post.userId!,
        post.profilePicture!,
        post.userGender!
      );
      user.coverPhoto = this.getUserProfilePicture(
        post.userId!,
        post.coverPhoto!,
        post.userGender!,
        true
      );
    }
    return user;
  };

  convertHTMLToPlainText(html: string) {
    var tempDivElement = document.createElement("div");
    tempDivElement.innerHTML = html;
    return tempDivElement.textContent || tempDivElement.innerText || "";
  }

  toolbarOptions = [
    [{ header: [1, 2, 3] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ script: "sub" }, { script: "super" }], // superscript/subscript
    [{ color: [] }, { background: [] }], // dropdown with defaults from theme
    // [{ 'font': [] }],
    ["clean"],
  ];

  getFeelingsJSON() {
    return feelingsJSON;
  }

  getAxios = (url: string, contentType: string = "application/json") => {
    return axios.create({
      baseURL: url,
    });
  };

  getMetaDataObject = (object: any) => {
    if (
      object["og:description"] ||
      object["description"] ||
      object["og:site_name"] ||
      object["source"] ||
      object["ogv-tooltip"] ||
      object["title"] ||
      object["og:image"] ||
      object["image"] ||
      object["og:url"] ||
      object["url"]
    ) {
      var meta = {
        description:
          object["og:description"] != ""
            ? object["og:description"]
            : object["description"],
        siteName:
          object["og:site_name"] != ""
            ? object["og:site_name"]
            : object["source"],
        title:
          object["ogv-tooltip"] != "" ? object["ogv-tooltip"] : object["title"],
        image: object["og:image"] != "" ? object["og:image"] : object["image"],
        url: object["og:url"] != "" ? object["og:url"] : object["url"],
      };
      if (meta.image && !meta.image.includes("http")) {
        meta.image = null;
      }
      return meta;
    }
    return null;
  };

  status = [
    {
      name: "profileDetails.labels.Open",
      value: "Open",
      color: "#8B77CF",
      isActive: true,
    },
    {
      name: "profileDetails.labels.Close",
      value: "Close",
      color: "#FB267B",
      isActive: false,
    },
    {
      name: "profileDetails.labels.High",
      value: "High",
      color: "#ffb21e",
      isActive: false,
    },
    {
      name: "profileDetails.labels.Low",
      value: "Low",
      color: "#FF3B3B",
      isActive: false,
    },
  ];

  getChartType(val: string, market: string) {
    if (market) {
      market = market.toLowerCase();
    }
    return JSON.parse(
      JSON.stringify(ChartTypesJSON.find((x) => x.value == val))
    );
  }

  readonly chartDuartions = [
    "1d",
    "5d",
    "1mo",
    "3mo",
    "6mo",
    "1y",
    // "2y",
    // "5y",
    // "10y",
    // "ytd",
    // "max",
  ];

  //   [
  //     1Day		5Minutes
  //     5Day		30Minutes
  //     1Month		1Hour
  //     3Month		4Hour
  //     6Month		1Day
  //     1Year		1 Wk
  // ]

  readonly intervals = [
    // { value: "1m", durations: ["1d"] },
    // { value: "2m", durations: ["1d"] },
    { value: "1h", durations: ["1d"] },
    { value: "1d", durations: ["1mo"] },
    // { value: "4h", durations: ["3mo"] },
    { value: "1d", durations: ["6mo"] },
    { value: "1d", durations: ["1y"] },
    { value: "15m", durations: ["1d"] },
    { value: "30m", durations: ["1d"] },
    // { value: "60m", durations: ["1d"] },
    { value: "1h", durations: ["1d", "5d"] },
    { value: "1d", durations: ["5d", "1mo", "2y", "ytd"] },
    { value: "5d", durations: [] },
    {
      value: "1wk",
      durations: ["1mo", "3mo", "6mo", "2y", "5y", "10y", "ytd"],
    },
    { value: "1mo", durations: ["6mo", "1y", "2y", "5y", "10y", "ytd", "max"] },
    { value: "3mo", durations: ["6mo", "1y", "2y", "5y", "10y", "ytd", "max"] },
  ];

  readonly chartDataTypes = ["Open", "High", "Low", "Close", "Volume"];

  getChartQuery = (
    symbol: string,
    duration: string = "",
    interval: string = "",
    dataType: any[] = [],
    stockType: string = "",
    stockId: string = "",
    chartType: string = ""
  ) => {
    let resolvedDataType = dataType;
    if (chartType === "candlestick" && (!dataType || !dataType.length)) {
      resolvedDataType = [
        { value: "Open", isActive: true },
        { value: "High", isActive: true },
        { value: "Low", isActive: true },
        { value: "Close", isActive: true },
      ];
    }
    let resolvedStockType = stockType;
    if (
      chartType === "candlestick" &&
      stockType?.toLowerCase() === StockMarkets.crypto
    ) {
      resolvedStockType = StockMarkets.stocks;
    }
    var query = "?";
    if (symbol) {
      query += `symbol=${symbol}`;
    }
    if (stockId) {
      query += `&stockId=${stockId}`;
    }
    if (duration) {
      query += `&duration=${duration}`;
    }
    if (interval) {
      query += `&interval=${interval}`;
    }
    if (resolvedStockType) {
      query += `&stockType=${resolvedStockType}`;
    }
    if (dataType.length) {
      const selectedDataTypes = dataType
        .filter((x) => typeof x == "string" || x.isActive)
        .map((x) => (typeof x == "string" ? x : x.value))
        .filter(Boolean)
        .join();

      if (selectedDataTypes) {
        query += `&dataType=${selectedDataTypes}`;
      }
    }
    return query;
  };

  getChartData(type: string) {
    if (type == "candlestick") {
      return [
        {
          data: [
            [
              1657200600000,
              [
                265.1199951171875, 269.05499267578125, 265.0199890136719,
                268.80999755859375,
              ],
            ],
            [
              1657204200000,
              [
                268.82000732421875, 268.9241943359375, 267.29998779296875,
                267.3599853515625,
              ],
            ],
            [
              1657207800000,
              [
                267.364990234375, 268.17498779296875, 267.04998779296875,
                267.7149963378906,
              ],
            ],
            [
              1657211400000,
              [
                267.7200012207031, 268.0400085449219, 267.260009765625,
                267.4150085449219,
              ],
            ],
            [
              1657215000000,
              [
                267.3999938964844, 268.3399963378906, 267.29998779296875,
                268.32000732421875,
              ],
            ],
            [
              1657218600000,
              [
                268.32000732421875, 268.9800109863281, 267.6400146484375,
                268.3399963378906,
              ],
            ],
            [
              1657222200000,
              [
                268.3301086425781, 268.7699890136719, 267.8399963378906,
                268.44000244140625,
              ],
            ],
            [
              1657224000000,
              [
                268.3999938964844, 268.3999938964844, 268.3999938964844,
                268.3999938964844,
              ],
            ],
          ],
        },
      ];
    }
    return [
      {
        name: "Open",
        data: [
          { x: 1657200600000, y: 265.1199951171875 },
          { x: 1657204200000, y: 268.82000732421875 },
          { x: 1657207800000, y: 267.364990234375 },
          { x: 1657211400000, y: 267.7200012207031 },
          { x: 1657215000000, y: 267.3999938964844 },
          { x: 1657218600000, y: 268.32000732421875 },
          { x: 1657222200000, y: 268.3301086425781 },
          { x: 1657224000000, y: 268.3999938964844 },
        ],
      },
      {
        name: "High",
        data: [
          { x: 1657200600000, y: 269.05499267578125 },
          { x: 1657204200000, y: 268.9241943359375 },
          { x: 1657207800000, y: 268.17498779296875 },
          { x: 1657211400000, y: 268.0400085449219 },
          { x: 1657215000000, y: 268.3399963378906 },
          { x: 1657218600000, y: 268.9800109863281 },
          { x: 1657222200000, y: 268.7699890136719 },
          { x: 1657224000000, y: 268.3999938964844 },
        ],
      },
      {
        name: "Low",
        data: [
          { x: 1657200600000, y: 265.0199890136719 },
          { x: 1657204200000, y: 267.29998779296875 },
          { x: 1657207800000, y: 267.04998779296875 },
          { x: 1657211400000, y: 267.260009765625 },
          { x: 1657215000000, y: 267.29998779296875 },
          { x: 1657218600000, y: 267.6400146484375 },
          { x: 1657222200000, y: 267.8399963378906 },
          { x: 1657224000000, y: 268.3999938964844 },
        ],
      },
      {
        name: "Close",
        data: [
          { x: 1657200600000, y: 268.80999755859375 },
          { x: 1657204200000, y: 267.3599853515625 },
          { x: 1657207800000, y: 267.7149963378906 },
          { x: 1657211400000, y: 267.4150085449219 },
          { x: 1657215000000, y: 268.32000732421875 },
          { x: 1657218600000, y: 268.3399963378906 },
          { x: 1657222200000, y: 268.44000244140625 },
          { x: 1657224000000, y: 268.3999938964844 },
        ],
      },
    ];
  }

  getStock() {
    return {
      key: {
        symbol: "MSFT",
        exchange: "NGS",
        exLgName: "Nasdaq Global Select",
        exShName: "NGS",
      },
      equityinfo: { longname: "Microsoft Corporation", shortname: "MSFT" },
      status: {},
      pricedata: {
        last: 268.4,
        change: 2.19,
        changepercent: 0.822659,
        tick: 0,
        open: 265.12,
        high: 269.055,
        low: 265.02,
        prevclose: 266.21,
        bid: 267.78,
        ask: 268,
        bidsize: 200,
        asksize: 2100,
        rawbidsize: 2,
        rawasksize: 21,
        tradevolume: 279382,
        sharevolume: 20839923,
        vwap: 267.957163,
        lasttradedatetime: "2022-07-07T16:00:00-04:00",
        lastquotedatetime: "2022-07-07T17:11:37-04:00",
        totalvalue: 5584610420,
        lastmarketidentificationcode: "NSD",
        bidmarketidentificationcode: "ARCA",
        askmarketidentificationcode: "ARCA",
        close: 268.4,
        imbalance: { imbalancetype: "None", imbalancesize: 0 },
      },
      fundamental: {
        sharesoutstanding: 7479033135,
        shareclasslevelsharesoutstanding: 7479033135,
        totalsharesoutstanding: 7479033135,
        marketcap: 2007372493434,
        eps: 9.57999992,
        peratio: 27.8,
        pbratio: 12.323,
        beta: 0.9330639839172363,
        week52high: { date: "2021-11-22", content: 349.67 },
        week52low: { date: "2022-06-14", content: 241.51 },
        week52highvwap: { date: "2021-11-22", content: 343.787009 },
        week52lowvwap: { date: "2022-06-14", content: 243.839082 },
        week52highvol: { date: "2022-01-26", content: 90428853 },
        week52lowvol: { date: "2021-08-05", content: 13873604 },
        week52avgvol: 29703520,
        dividend: {
          date: "2022-08-17",
          amount: 2.48,
          yield: 0.932,
          latestamount: { currency: "USD", content: 0.62 },
          frequency: "Q",
          paydate: "2022-09-08",
        },
        sharesescrow: 0,
        avgvol30days: 29051569,
        float: 7475126855,
      },
      classification: { sector: "Technology", industry: "Software" },
      symbolstring: "MSFT",
      datatype: "equity",
      entitlement: "RT",
      datetime: "2022-07-07T17:11:42-04:00",
    };
  }

  goToProfileScreen = (userName: string) => {
    let info: any = storageService.getUser();
    router.push({
      name: RouteEnum.profile,
      params: { username: info.userName },
    });
  };

  goToProfileScreenTwo = (userName: string) => {
    // let info: any = storageService.getUser();
    router.push({
      name: RouteEnum.profile,
      params: { username: userName },
    });
  };

  allRelations = [
    { name: "Friend", value: "Friend", icon: "friend" },
    { name: "Online Friend", value: "Online Friend", icon: "onlinefriend" },
    { name: "Colleague", value: "Colleague", icon: "colleague" },
    { name: "Relative", value: "Relative", icon: "relative" },
  ];

  liveUpdateDuration = 20000;

  generateSeriesForChart(series: any[]) {
    return series;
  }

  chartColor = {
    red: "#ef403c",
    green: "#00b746",
  };

  minDate = new Date().toISOString().split("T")[0];

  validateNumberInput(e: any) {
    e = e || window.event;
    var charCode = typeof e.which == "undefined" ? e.keyCode : e.which;
    var charStr = String.fromCharCode(charCode);
    if (!charStr.match(/^[0-9]+$/)) e.preventDefault();
  }

  isMobileView() {
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    ) {
      return true;
    } else {
      if (window.screen.width < 768) {
        return true;
      }
      return false;
    }
  }

  isTabletView(): boolean {
    const ua = navigator.userAgent.toLowerCase();

    const isIpad =
      /\b(iPad)\b/.test(ua) ||
      (navigator.userAgent.includes("Macintosh") && "ontouchend" in document);

    const isAndroidTablet = /android/.test(ua) && !/mobile/.test(ua);
    return isIpad || isAndroidTablet || window.innerWidth < 1280;
  }

  alignPostByResponsiveness = (
    elemId: string,
    configuration: ConfigurationModel,
    forceColWidth: number = 0
  ) => {
    var feedList = document.getElementById(elemId);
    if (feedList) {
      const style = window.getComputedStyle(feedList);
      const paddingRight = style.paddingRight;
      const paddingLeft = style.paddingLeft;
      if (forceColWidth > 0) {
        this.widthOfCol = forceColWidth;
      } else {
        this.widthOfCol = this.initialColWidth;
      }
      this.setWidth(
        feedList.offsetWidth,
        configuration,
        feedList.clientWidth -
          +paddingLeft.replace("px", "") -
          +paddingRight.replace("px", "")
      );
    }
  };

  setWidth = (
    screenWidth: number,
    configuration: ConfigurationModel,
    maxContainerWidth: number
  ) => {
    let totalCount = configuration.activitiesCount;

    while (true) {
      const postTotalMargin = this.margin * (totalCount - 1);
      let oneColWidth = (screenWidth - postTotalMargin) / totalCount;
      // Cap the column width
      if (oneColWidth > 700) {
        oneColWidth = 700;
      }
      if (oneColWidth > maxContainerWidth) {
        oneColWidth = maxContainerWidth;
      }
      // If acceptable width, set config and break
      if (oneColWidth >= this.widthOfCol || totalCount <= 1) {
        configuration.maxWidth = oneColWidth + "px";
        configuration.activitiesCount = totalCount;
        configuration.widgetsCount = 0;
        break;
      }
      // Otherwise, reduce the count and retry
      totalCount--;
    }
  };

  showDeleteConfirmationPopup = (title: string) => {
    return new Promise(async (resolve, reject) => {
      const modal = await openModal(AlertDeleteComponent, {
        title: title,
      });
      modal.on("close", (val: boolean) => {
        resolve(val);
      });
    });
  };
  convertToTitleCase(val: string) {
    return val
      .split(/\s+/) // Split by one or more whitespace characters
      .filter((w) => w.length > 0) // Remove empty strings
      .map((w) => w[0]?.toUpperCase() + w.substring(1).toLowerCase())
      .join(" ");
  }
  goToCryptoScreen = (stockId: string) => {
    router.push({
      name: "crypto",
      params: { stock: stockId },
    });
  };

  getUserFromProfile = (
    profile: ConnectionModel,
    isParsed: boolean = false
  ) => {
    var user: any = new User();
    var profileUser = userProfileUser().profileUser;
    var isOtherProfile: boolean = profileUser.value._id === profile.userId;
    user.fullName = isOtherProfile ? profile.connectionName : profile.userName;
    user.userName = isOtherProfile
      ? profile.connectionUsername
      : profile.userName;
    user.country = isOtherProfile
      ? profile.connectionCountry
      : profile.userCountry;
    user.gender = isOtherProfile
      ? profile.connectionGender
      : profile.userGender;
    user._id = isOtherProfile ? profile.connectionId : profile.userId;
    var media = this.generateImages(profile, isParsed);
    user.profilePicture = media.profilePicture;
    user.coverPhoto = media.coverPicture;
    return user;
  };

  generateImages(profile: ConnectionModel, isParsed: boolean = false) {
    var profileUser = storageService.getUser();
    var profileImg = "";
    var coverImg = "";
    if (profileUser._id === profile.userId) {
      profileImg = isParsed
        ? this.getUserProfilePicture(
            profile.connectionId!,
            profile.connectionProfilePicture!,
            profile.connectionGender!
          )
        : profile.connectionProfilePicture!;
      coverImg = isParsed
        ? this.getUserProfilePicture(
            profile.connectionId!,
            profile.connectionCoverPhoto!,
            profile.connectionGender!,
            true
          )
        : profile.connectionCoverPhoto!;
    } else {
      profileImg = isParsed
        ? this.getUserProfilePicture(
            profile.userId!,
            profile.userProfilePicture!,
            profile.userGender!
          )
        : profile.userProfilePicture!;
      coverImg = isParsed
        ? this.getUserProfilePicture(
            profile.userId!,
            profile.userCoverPhoto!,
            profile.userGender!,
            true
          )
        : profile.userCoverPhoto!;
    }
    return {
      profilePicture: profileImg,
      coverPicture: coverImg,
    };
  }

  camelCaseToWords(text: string) {
    const result = text.replace(/([A-Z])/g, " $1");
    return result.charAt(0)?.toUpperCase() + result.slice(1);
  }

  numberFormatter(options = null) {
    return new Intl.NumberFormat(
      "en-US",
      options || {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
        // roundingIncrement: 5,
        // notation: "compact",
        unitDisplay: "short",
      }
    );
  }

  formateValue(value) {
    const absValue = Math.abs(value);
    const sign = value < 0 ? "-" : "";

    if (absValue < 1000) {
      return sign + absValue.toString();
    } else if (absValue < 100000) {
      return sign + (absValue / 1000).toFixed(1) + "K";
    } else if (absValue < 1000000000) {
      return sign + (absValue / 1000000).toFixed(2) + "M";
    } else {
      return sign + (absValue / 1000000000).toFixed(2) + "B";
    }
  }

  isValidDate(d: Date) {
    return d instanceof Date && !isNaN(d as any);
  }

  dateFormatter(
    locale = "en-GB",
    date: any,
    options: Intl.DateTimeFormatOptions = {}
  ) {
    const dateObj = new Date(date); // Ensure it's a Date object
    if (this.isValidDate(dateObj)) {
      return new Intl.DateTimeFormat(locale, options).format(dateObj);
    }
    return null;
  }

  commaSeperatedValue(value: any) {
    if (value) {
      return value.toLocaleString() ? value.toLocaleString() : 0;
    }
    return 0;
  }

  compactNumberFormat(value) {
    if (value === null || value === undefined || isNaN(value)) return "-";

    const absValue = Math.abs(value);
    let formattedValue;
    let suffix = "";

    if (absValue >= 1e12) {
      formattedValue = (value / 1e12).toFixed(2);
      suffix = "T";
    } else if (absValue >= 1e9) {
      formattedValue = (value / 1e9).toFixed(2);
      suffix = "B";
    } else if (absValue >= 1e6) {
      formattedValue = (value / 1e6).toFixed(2);
      suffix = "M";
    } else if (absValue >= 1e3) {
      formattedValue = (value / 1e3).toFixed(2);
      suffix = "K";
    } else {
      formattedValue = value.toFixed(2);
    }

    formattedValue = formattedValue.replace(/\.00$/, "");

    return `${formattedValue}${suffix}`;
  }

  calculateSizeOfLayoutColumn = (size: string, parentWidget: any): string => {
    const clientWidth = (parentWidget!["clientWidth"] - 7) as number;
    const sizes = ["small", "medium", "large"];
    if (size) {
      size = size.toLowerCase();
    } else {
      size = sizes[0];
    }
    if (size == sizes[2] && clientWidth < 1200) {
      return this.calculateSizeOfLayoutColumn(sizes[1], parentWidget);
    } else if (size == sizes[1] && clientWidth < 800) {
      return sizes[0];
    }
    return size;
  };

  calculateWidthOfLayoutColumn = (
    size: string,
    parentWidget: any,
    columnCount: number
  ): number => {
    const clientWidth = (parentWidget!["clientWidth"] - 7) as number;
    const sizes = ["small", "medium", "large"];
    if (size) {
      size = size.toLowerCase();
    } else {
      size = sizes[0];
    }
    if (size == sizes[2] && clientWidth < 1200) {
      return this.calculateWidthOfLayoutColumn(
        sizes[1],
        parentWidget,
        columnCount
      );
    } else if (size == sizes[1] && clientWidth < 800) {
      return this.calculateWidthOfLayoutColumn(
        sizes[0],
        parentWidget,
        columnCount
      );
    } else {
      var totalColumns = columnCount;
      if (totalColumns == 3 && clientWidth < 1200) {
        totalColumns -= 1;
      } else if (totalColumns == 2 && clientWidth < 800) {
        totalColumns -= 1;
      }
      var wd = clientWidth / totalColumns;
      var widthPerColumn = this.widthOfCol;
      var finalWidth = this.widthOfCol;
      if (size == sizes[2]) {
        finalWidth = widthPerColumn * 3 + this.margin * 2;
      } else if (size == sizes[1]) {
        finalWidth = widthPerColumn * 2 + this.margin;
      } else {
        finalWidth = widthPerColumn;
      }
      return finalWidth;
    }
  };

  updateUserAfterConfiguration = async (user: any) => {
    try {
      const response = await httpService.updateUserAfterConfiguration(
        user.configuration
      );
      if (response.isSuccess) {
        storageService.setUser(response.data);
        return true;
      }
      return;
    } catch (e) {
      return;
    }
  };

  mountProductierWidget = (user: any) => {
    if (!user) {
      return;
    }

    if ((window as any).ProductierReportWidget) {
      const userData = {
        user_id: user._id,
        user_email: user.email,
        user_name: user.fullName,
      } as any;

      //     (window as any).userSessionData = userData;

      //     (window as any).ProductierReportWidget.mount({
      //       targetId: 'productier-widget-container',
      //       apiKey: '76ae73e5-4bff-44f6-a84b-efbaac6a3019',
      //       metadata: userData,
      //       position: 'bottom-left'
      //     })
    }
  };

  readonly SupportedFiles = [
    // office viewed Files
    "csv",
    "xls",
    "xlsx",
    "pptx",
    "ppt",
    "doc",
    "docx",
    "pdf",
    "txt",
    "ai",
    "eps",
    // images
    "jpg",
    "jpeg",
    "png",
    "gif",
    "jfif",
    // video
    "mp4",
    "mov",
    "3gp",
    "avi",
  ];

  mapTime(timestamp: number, addDate: boolean = false) {
    try {
      const date = new Date(timestamp);
      const currentDate = new Date();

      // Get time in 12-hour format with AM/PM
      const timeVal = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }); // e.g., "12:09 AM"

      // Get date in "Apr 10" format
      const dateVal = date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
      }); // e.g., "Apr 10"

      if (addDate) {
        if (
          currentDate.getDate() === date.getDate() &&
          currentDate.getMonth() === date.getMonth() &&
          currentDate.getFullYear() === date.getFullYear()
        ) {
          return timeVal;
        } else {
          return `${dateVal}, ${timeVal}`;
        }
      } else {
        return `${dateVal}`;
      }
    } catch (e) {
      return "";
    }
  }

  goToConversationScreen = (chatId: string) => {
    router.push({
      name: "socialize_chats_chat",
      params: {
        chatId: chatId,
      },
    });
  };

  byteConverter(bytes: any, decimals: any) {
    const K_UNIT = 1024;
    const SIZES = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

    if (bytes == 0) return "0 Byte";
    let i = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
    let resp =
      parseFloat((bytes / Math.pow(K_UNIT, i)).toFixed(decimals)) +
      " " +
      SIZES[i];

    return resp;
  }

  draggableScrollTwo(ele: any) {
    try {
      if (ele && ele.length) {
        ele = ele[0];
      }
      if (ele) {
        ele.style.cursor = "grab";

        let pos = { top: 0, left: 0, x: 0, y: 0 };

        const mouseDownHandler = function (e: any) {
          e.stopPropagation();
          ele.style.cursor = "grabbing";
          ele.style.userSelect = "none";

          pos = {
            left: ele.scrollLeft,
            top: ele.scrollTop,
            // Get the current mouse position
            x: e.clientX,
            y: e.clientY,
          };

          document.addEventListener("mousemove", mouseMoveHandler);
          document.addEventListener("mouseup", mouseUpHandler);
        };

        const mouseMoveHandler = function (e: any) {
          e.stopPropagation();
          // How far the mouse has been moved
          const dx = e.clientX - pos.x;
          // const dy = e.clientY - pos.y;

          // Scroll the element
          // ele.scrollTop = pos.top - dy;
          ele.scrollLeft = pos.left - dx;
        };

        const mouseUpHandler = function () {
          ele.style.cursor = "grab";
          ele.style.removeProperty("user-select");

          document.removeEventListener("mousemove", mouseMoveHandler);
          document.removeEventListener("mouseup", mouseUpHandler);
        };

        // Attach the handler
        ele.addEventListener("mousedown", mouseDownHandler);
      }
    } catch (e) {
      console.log(e);
    }
  }

  progressLong(entryPrice, currentPrice, targetPrice) {
    const numerator = currentPrice - entryPrice;
    const denominator = targetPrice - entryPrice;
    const progress = Math.min(1, Math.max(0, numerator / denominator)) * 100;
    return progress;
  }

  progressShort(entryPrice, currentPrice, targetPrice) {
    const numerator = entryPrice - currentPrice;
    const denominator = entryPrice - targetPrice;
    const progress = Math.min(1, Math.max(0, numerator / denominator)) * 100;
    return progress;
  }

  timeout;
  handle401Error = () => {
    if (this.isLoggingOut) {
      return;
    }
    if (isAccountBannedSession() || isAccountDeletedSession()) {
      return;
    }
    const routeName = router.currentRoute.value?.name;
    if (routeName === "accountBanned" || routeName === "accountDeleted") {
      return;
    }
    this.isLoggingOut = true;
    // try {
    //   let modalCloseAttempts = 0;
    //   const maxAttempts = 10;
    //   while (modalCloseAttempts < maxAttempts) {
    //     try {
    //       popModal();
    //       modalCloseAttempts++;
    //     } catch (e) {
    //       break;
    //     }
    //   }
    // } catch (e) {
    //   console.warn("Error closing modals during logout:", e);
    // }
    // this.showToast("Session expired. Please login again.", TYPE.ERROR);
    this.timeout = setTimeout(() => {
      this.cancelPendingRequests();
      storageService.RemoveAllProperties();
      if (
        typeof window !== "undefined" &&
        window.location.pathname !== "/signin"
      ) {
        setPostLoginRedirect(
          `${window.location.pathname}${window.location.search}${window.location.hash}`
        );
        router.push({ name: "signin" });
      }
    }, 1000);
  };

  // Method to cancel pending HTTP requests during logout
  cancelPendingRequests = () => {
    // This will be used by axios interceptors to cancel ongoing requests
  };

  // Method to reset logout state (useful for testing)
  resetLogoutState = () => {
    this.isLoggingOut = false;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }
  };

  // Method to check if we're in logout flow
  isInLogoutFlow = () => {
    return this.isLoggingOut;
  };

  // Test method to simulate session expiry (for testing purposes)
  simulateSessionExpiry = () => {
    this.handle401Error();
  };

  getStatsValue = (key: string, value: number): string => {
    // Add null check for key parameter
    if (!key) {
      return value.toString();
    }

    // Implement your specific formatting logic here
    // This is a placeholder - adjust according to your needs
    if (key && (key.includes("Percent") || key.includes("Rate"))) {
      return `${value.toFixed(2)}%`;
    }
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  };
}

export default new UtilService();
